/**
 * L'écran des ordres de transport — la liste, la fiche, la création.
 *
 * Conforme à la maquette validée `docs/maquettes/ot.html`, aux écarts près qui
 * sont écrits plus bas et qui tiennent tous à la même cause : la maquette
 * montre des choses que la base ne porte pas encore.
 *
 * Même découpe que les autres écrans, et pour la même raison :
 *
 *   - `VueOrdres` ne fait que DESSINER. On lui donne un état, elle rend du
 *     HTML — ce qui permet de la rendre et de la REGARDER hors navigateur,
 *     avec n'importe quel état : liste vide, fiche en panne, étape en litige ;
 *   - `EcranOrdres` tient l'état, appelle l'API, et lui passe le résultat.
 *
 * Le calcul est dans `ot.ts`, en fonctions pures : mise en forme, totaux,
 * filtrage, contrôle de saisie, composition des instants.
 *
 * TROIS ÉCARTS AVEC LA MAQUETTE, TOUS ASSUMÉS ET SIGNALÉS À L'UTILISATEUR :
 *
 *   - **l'onglet Documents A OUVERT le 04/09/2026** (migration
 *     `20260904190000`). Le fichier ne passe pas par l'API : le navigateur
 *     l'envoie au rangement Supabase avec sa propre session, et l'API n'écrit
 *     que la ligne. La ligne D'ABORD, le fichier ensuite — un fichier sans
 *     ligne serait invisible et payant ;
 *   - **« Ressource affectée » n'est pas sur les étapes.** Elle viendra du
 *     planning, qui n'existe pas : aucune colonne ne relie aujourd'hui une
 *     étape à une affectation. L'afficher demanderait d'inventer le lien ;
 *   - **« Dupliquer » n'est pas là.** Il n'existe aucune route qui duplique un
 *     ordre, et l'imiter par cinq appels laisserait un ordre à moitié copié si
 *     le troisième échouait.
 *
 * Un quatrième écart, dans l'autre sens : **la fiche porte « Annuler » et
 * « Rouvrir »**, que la maquette ne montre pas. Ce sont les deux seules
 * décisions humaines qui restent sur le statut — les quatre autres valeurs se
 * déduisent des étapes depuis le 04/09/2026. Le sélecteur de statut qui vivait
 * dans le formulaire de modification a été retiré le même jour : il posait une
 * valeur que la première étape validée réécrivait sans prévenir.
 */

import { useCallback, useEffect, useState } from 'react';
import { ANNUAIRE_TRANSPORTEURS } from './transporteurs.js';
import { FormulaireFiche } from './FormulaireFiche.js';
import {
  codePropose,
  pourCreation,
  saisieVide,
  validerFiche,
  type Saisie,
} from './annuaire.js';
import {
  manquesDits,
  margeReelle,
  margeReelleDite,
  minutesDites,
  minutesSaisies,
} from './finance.js';
import {
  AFFRETEMENT_VIDE,
  CHOIX_NOUVEAU,
  corpsAffretement,
  grouperTransporteurs,
  marge,
  margeDite,
  type SaisieAffretement,
} from './affretement.js';
import {
  ajouterEtape,
  annulerOrdre,
  creerFiche,
  lireListe,
  lireLeCout,
  ouvrirUnLitige,
  poserLExecution,
  recalculerLeCout,
  resoudreUnLitige,
  lireListeReglee,
  lireMotifsLitige,
  lireOrdre,
  affreter,
  annulerLAffretement,
  chercherTransporteurs,
  poserLEchange,
  type TransporteurTrouve,
  lireOrdres,
  lireParc,
  lireVehicules,
  creerMotifLitige,
  modifierEtape,
  modifierOrdre,
  creerOrdre,
  reordonnerEtapes,
  rouvrirOrdre,
  supprimerEtape,
  supprimerOrdre,
  lireFiche,
  type EchecApi,
  type CoutOrdre,
  type LigneListe,
  type Resultat,
  type Vehicule,
  type Document as DocumentJoint,
  type LigneReglee,
  confirmerLeDocument,
  declarerLeDocument,
  lireDocuments,
  supprimerLeDocument,
} from './api.js';
import {
  adresseDuFichier,
  effacerLeFichier,
  envoyerLeFichier,
  poidsDit,
  POIDS_MAXIMUM,
} from './rangement.js';
import { ANNUAIRE_SITES } from './sites.js';
import { PARC_CHAUFFEURS, PARC_REMORQUES, PARC_VEHICULES } from './parcs.js';
import { texte as texteParc, type LigneParc } from './parc.js';
import { ANNUAIRE_CLIENTS } from './clients.js';
import { valeur } from './annuaire.js';
import {
  adresseDite,
  aujourdhui,
  classeStatut,
  corpsEtape,
  creneau,
  chargementDit,
  corpsEchange,
  ecartEchange,
  echangeDepuis,
  decompte,
  differenceOrdre,
  enEuros,
  enFrancais,
  etapeDepuis,
  etapeVide,
  filtrer,
  instantDepuis,
  instantDit,
  jourCourt,
  lisible,
  litigeDit,
  ordreDepuis,
  ORDRE_VIDE,
  pourCreationOrdre,
  pourCreationSite,
  maintenant,
  ranger,
  rapport,
  refusEchange,
  refusLitige,
  ressourceDite,
  refusValidation,
  reporterSurLesLivraisons,
  SITE_VIDE,
  affreteDit,
  estAffrete,
  statutDit,
  STATUTS,
  metreLineaireDit,
  supportsAvec,
  totaux,
  livraisonDit,
  validerEtape,
  validerOrdre,
  validerSite,
  type Etape,
  type FicheOrdre,
  type MotifLitige,
  type OrdreTransport,
  type SaisieEchange,
  type SaisieEtape,
  type SaisieLitige,
  type SaisieOrdre,
  type SaisieSite,
  type SaisieValidation,
  type TypeEtape,
} from './ot.js';

// ---------------------------------------------------------------------------
// Les états
// ---------------------------------------------------------------------------

type EtatListe =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'prete'; readonly ordres: readonly OrdreTransport[] }
  | { readonly nom: 'panne' };

type EtatFiche =
  | { readonly nom: 'aucune' }
  | { readonly nom: 'chargement'; readonly id: string }
  | { readonly nom: 'prete'; readonly id: string; readonly fiche: FicheOrdre }
  | { readonly nom: 'panne'; readonly id: string; readonly cause: EchecApi };

type Onglet = 'commande' | 'etapes' | 'documents';

/** L'édition de la commande, dans la fiche comme à la création. */
interface EditionOrdre {
  readonly initiale: SaisieOrdre;
  readonly saisie: SaisieOrdre;
  readonly fautes: Readonly<Record<string, string>>;
  readonly refus: string | null;
  readonly envoi: boolean;
}

/**
 * L'étape ouverte en saisie.
 *
 * `id` vaut `null` pour une étape qu'on ajoute : elle n'existe pas encore en
 * base, et le formulaire est le même. `site` porte la création d'un site
 * repliée dans la carte — `null` tant qu'on ne l'a pas dépliée.
 */
interface EditionEtape {
  readonly id: string | null;
  readonly saisie: SaisieEtape;
  /**
   * L'étape telle qu'elle était à l'ouverture. Sert à n'envoyer QUE ce qui a
   * changé sur l'équipage : poser un camion sans toucher au chauffeur laisse
   * l'API recopier l'attelage du jour, ce qu'un « chauffeur : rien » explicite
   * empêcherait.
   */
  readonly initiale: SaisieEtape;
  readonly fautes: Readonly<Record<string, string>>;
  readonly refus: string | null;
  readonly envoi: boolean;
  readonly site: SaisieSite | null;
  readonly fautesSite: Readonly<Record<string, string>>;
  readonly confirme: boolean;
}

/** Ce que l'API a refusé, dit en français. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable:
    'Cet ordre de transport n’existe plus. Il a pu être supprimé entre-temps.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce numéro est déjà pris. Réessayez.',
  invalide: 'Une valeur saisie n’est pas acceptée. Vérifiez les champs.',
  // Un ordre de transport ne pose aucune période : ce refus ne vient jamais
  // d'ici. La clé existe parce que `EchecApi` la porte.
  conflit: 'Cette période en recouvre une autre.',
  encore_actif: 'Annulez cet ordre avant de le supprimer.',
  encore_rattache: 'Impossible de supprimer : quelque chose s’y rattache encore.',
  // Les quatre refus du planning. Ils n'arrivent pas sur cet écran — la carte
  // doit rester exhaustive, c'est ce qui garantit qu'aucun refus de l'API ne
  // s'affichera un jour sous forme de code brut.
  ordre_annule: 'Cet ordre est annulé : il ne se planifie pas.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape : rien à poser sur un camion.',
  etape_validee:
    'Une étape de cet ordre est déjà validée : ses dates ne se déplacent plus.',
  jour_sans_etape: 'Cet ordre n’a aucune étape ce jour-là. Rechargez l’écran.',
  // Ne vient jamais d'ici : seul le planning déplace des étapes. La carte doit
  // rester exhaustive — c'est ce qui garantit qu'aucun refus de l'API ne
  // s'affichera un jour sous forme de code brut.
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  litige_deja_ouvert:
    'Cette étape a déjà un litige ouvert : résolvez-le avant d’en ouvrir un autre.',
  litige_deja_resolu:
    'Ce litige est déjà résolu. Ouvrez-en un nouveau sur l’étape si l’incident se répète.',
};

// ---------------------------------------------------------------------------
// Petits morceaux de dessin
// ---------------------------------------------------------------------------

function Statut({ statut }: { readonly statut: string }) {
  return <span className={`statut ${classeStatut(statut)}`}>{statutDit(statut)}</span>;
}

/** Une paire libellé/valeur dans l'onglet Commande. Jamais de valeur inventée. */
function Champ({
  nom,
  children,
  calcule,
}: {
  readonly nom: string;
  readonly children: React.ReactNode;
  readonly calcule?: string | undefined;
}) {
  return (
    <div className="champ-ot">
      <dt>{nom}</dt>
      <dd className={calcule === undefined ? undefined : 'fige'}>
        {children}
        {calcule !== undefined && <span className="cadenas"> · {calcule}</span>}
      </dd>
    </div>
  );
}

function Absent() {
  return <span className="sourdine">non renseigné</span>;
}

/**
 * Le site tel qu'il s'écrit dans un déroulant : son nom, et sa ville s'il ne la
 * porte pas déjà.
 *
 * Constaté en REGARDANT l'écran le 04/09/2026 : « LE ROY LOGISTIQUE — Amiens »
 * s'affichait « LE ROY LOGISTIQUE — Amiens — Amiens ». Les sites réels de TNS
 * portent presque tous leur ville dans leur nom ; ajouter la colonne `ville`
 * sans regarder la répétait sur la moitié de la liste.
 */
export function nommerLeSite(site: {
  readonly nom: string;
  readonly ville: string | null;
}): string {
  if (site.ville === null || site.ville.trim() === '') return site.nom;
  const sansAccent = (t: string) =>
    t
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  return sansAccent(site.nom).includes(sansAccent(site.ville))
    ? site.nom
    : `${site.nom} — ${site.ville}`;
}

// ---------------------------------------------------------------------------
// Le formulaire d'une étape
// ---------------------------------------------------------------------------

export interface ProprietesFormulaireEtape {
  readonly edition: EditionEtape;
  readonly sites: readonly LigneListe[];
  readonly motifs: readonly MotifLitige[];
  /**
   * Les supports que la société a réglés, AVEC LEURS DIMENSIONS depuis le
   * 05/09/2026 : le formulaire n'en tire plus seulement des noms, il en tire
   * le mètre linéaire à afficher pendant la saisie. Ils ne sont plus écrits
   * dans le code
   * depuis le 04/09/2026 : ils descendent de l'API jusqu'ici.
   */
  readonly supports: readonly LigneReglee[];
  readonly vehicules: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  /** Vrai à la création de l'ordre : ni « fait », ni litige sur une étape à naître. */
  readonly neuve: boolean;
  readonly quandChamp: (cle: keyof SaisieEtape, valeur: string | boolean) => void;
  readonly quandSite: (id: string) => void;
  readonly quandOuvrirSite: () => void;
  readonly quandChampSite: (cle: keyof SaisieSite, valeur: string) => void;
  readonly quandCreerSite: () => void;
  readonly quandFermerSite: () => void;
}

export function FormulaireEtape({
  edition,
  sites,
  supports,
  vehicules,
  chauffeurs,
  remorques,
  neuve,
  quandChamp,
  quandSite,
  quandOuvrirSite,
  quandChampSite,
  quandCreerSite,
  quandFermerSite,
}: ProprietesFormulaireEtape) {
  const { saisie, fautes, site, fautesSite } = edition;

  const faute = (cle: string) =>
    fautes[cle] === undefined ? null : (
      <span className="erreur-champ">{fautes[cle]}</span>
    );

  return (
    <div className="grille-saisie">
      <div className="saisie c3">
        <label htmlFor="etape-site">Site *</label>
        <select
          id="etape-site"
          className="boite"
          value={saisie.site_id}
          onChange={(e) => quandSite(e.target.value)}
        >
          <option value="">Choisir un site…</option>
          {sites.map((s) => (
            <option value={s.id} key={s.id}>
              {nommerLeSite(s)}
            </option>
          ))}
        </select>
        {faute('site_id')}
      </div>
      <div className="sous-champ c3">
        {site === null && (
          <button
            className="bouton bouton-discret"
            type="button"
            onClick={quandOuvrirSite}
          >
            + Créer un site
          </button>
        )}
      </div>

      {/* Le site absent de l'annuaire se crée SANS QUITTER l'ordre : on
          n'abandonne pas une saisie à moitié faite pour aller ailleurs. Seul le
          nom est obligatoire — vérifié dans `sites.ts`, c'est le seul champ
          marqué `obligatoire` du descripteur. */}
      {site !== null && (
        <div className="creation-site">
          <p className="titre-creation-site">Créer le site</p>
          <div className="grille-saisie">
            <div className="saisie c3">
              <label htmlFor="site-nom">Nom *</label>
              <input
                id="site-nom"
                className="boite"
                type="text"
                value={site.nom}
                onChange={(e) => quandChampSite('nom', e.target.value)}
              />
              {fautesSite['nom'] !== undefined && (
                <span className="erreur-champ">{fautesSite['nom']}</span>
              )}
            </div>
            <div className="saisie c3">
              <label htmlFor="site-adresse">Adresse</label>
              <input
                id="site-adresse"
                className="boite"
                type="text"
                value={site.adresse}
                onChange={(e) => quandChampSite('adresse', e.target.value)}
              />
            </div>
            <div className="saisie c2">
              <label htmlFor="site-cp">Code postal</label>
              <input
                id="site-cp"
                className="boite"
                type="text"
                value={site.code_postal}
                onChange={(e) => quandChampSite('code_postal', e.target.value)}
              />
            </div>
            <div className="saisie c2">
              <label htmlFor="site-ville">Ville</label>
              <input
                id="site-ville"
                className="boite"
                type="text"
                value={site.ville}
                onChange={(e) => quandChampSite('ville', e.target.value)}
              />
            </div>
            <div className="saisie c2">
              <label htmlFor="site-code">Code</label>
              <input
                id="site-code"
                className="boite"
                type="text"
                placeholder="Facultatif"
                value={site.code}
                onChange={(e) => quandChampSite('code', e.target.value)}
              />
            </div>
          </div>
          <p className="aide-saisie">
            Le téléphone, le courriel, la note et les contacts se complètent plus tard
            depuis l’annuaire des sites : les demander ici retarderait la saisie de
            l’ordre.
          </p>
          <div className="pied-creation-site">
            <button className="bouton" type="button" onClick={quandCreerSite}>
              Créer et rattacher
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              onClick={quandFermerSite}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* L'adresse du jour. Recopiée de la fiche du site, puis FIGÉE — mais
          modifiable, parce que le camion a pu aller ailleurs. Les quatre noms
          sont ceux de l'annuaire des sites. */}
      <div className="saisie c2">
        <label htmlFor="etape-nom">Nom du lieu</label>
        <input
          id="etape-nom"
          className="boite"
          type="text"
          value={saisie.site_nom}
          onChange={(e) => quandChamp('site_nom', e.target.value)}
        />
        {faute('site_nom')}
      </div>
      <div className="saisie c2">
        <label htmlFor="etape-adresse">Adresse</label>
        <input
          id="etape-adresse"
          className="boite"
          type="text"
          value={saisie.site_adresse}
          onChange={(e) => quandChamp('site_adresse', e.target.value)}
        />
      </div>
      <div className="saisie">
        <label htmlFor="etape-cp">Code postal</label>
        <input
          id="etape-cp"
          className="boite"
          type="text"
          value={saisie.site_code_postal}
          onChange={(e) => quandChamp('site_code_postal', e.target.value)}
        />
      </div>
      <div className="saisie">
        <label htmlFor="etape-ville">Ville</label>
        <input
          id="etape-ville"
          className="boite"
          type="text"
          value={saisie.site_ville}
          onChange={(e) => quandChamp('site_ville', e.target.value)}
        />
      </div>

      {/* Une date se saisit dans un champ DATE, une heure dans un champ HEURE —
          demandé par l'utilisateur le 03/09/2026. Le calendrier du système
          s'ouvre, les flèches incrémentent, et « 32/13/2026 » ne peut pas être
          tapé. Le format suit la langue du NAVIGATEUR, pas le code. */}
      <div className="saisie">
        <label htmlFor="etape-jour">Date *</label>
        <input
          id="etape-jour"
          className="boite"
          type="date"
          value={saisie.jour}
          onChange={(e) => quandChamp('jour', e.target.value)}
        />
        {faute('jour')}
      </div>
      <div className="saisie">
        <label htmlFor="etape-de">De</label>
        <input
          id="etape-de"
          className="boite"
          type="time"
          value={saisie.heure_debut}
          onChange={(e) => quandChamp('heure_debut', e.target.value)}
        />
        {faute('heure_debut')}
      </div>
      <div className="saisie">
        <label htmlFor="etape-a">À</label>
        <input
          id="etape-a"
          className="boite"
          type="time"
          value={saisie.heure_fin}
          onChange={(e) => quandChamp('heure_fin', e.target.value)}
        />
        {faute('heure_fin')}
      </div>
      <div className="saisie">
        <label htmlFor="etape-quantite">Quantité</label>
        <input
          id="etape-quantite"
          className="boite"
          type="text"
          inputMode="decimal"
          value={saisie.quantite}
          onChange={(e) => quandChamp('quantite', e.target.value)}
        />
        {faute('quantite')}
      </div>
      {/* Liste FERMÉE à l'écran, base ouverte — demandé le 04/09/2026, et
          contraire au commentaire de la migration : voir `SUPPORTS` dans
          `ot.ts`, où la contradiction est écrite. */}
      <div className="saisie">
        <label htmlFor="etape-support">Support</label>
        <select
          id="etape-support"
          className="boite"
          value={saisie.support}
          onChange={(e) => quandChamp('support', e.target.value)}
        >
          <option value="">Sans support</option>
          {supportsAvec(
            saisie.support,
            supports.map((s) => s.nom),
          ).map((s) => (
            <option value={s} key={s}>
              {s}
            </option>
          ))}
        </select>
        {/* LE MÈTRE LINÉAIRE, PENDANT QU'ON SAISIT — demandé par l'utilisateur
            le 05/09/2026. Il ne se saisit jamais et n'apparaît dans aucun
            champ : c'est une lecture, calculée par le même code que celui qui
            le figera sur l'étape. Absent quand il n'y a rien à dire, plutôt
            qu'un « 0,00 ML » qui se lirait « n'occupe rien ». */}
        {metreLineaireDit(saisie.support, saisie.quantite, supports) !== null && (
          <p className="calcule">
            {metreLineaireDit(saisie.support, saisie.quantite, supports)}
          </p>
        )}
      </div>
      <div className="saisie">
        <label htmlFor="etape-poids">Poids (kg)</label>
        <input
          id="etape-poids"
          className="boite"
          type="text"
          inputMode="numeric"
          value={saisie.poids}
          onChange={(e) => quandChamp('poids', e.target.value)}
        />
        {faute('poids')}
      </div>

      {/* LE CAMION EST SUR L'ÉTAPE, et non sur l'ordre — décidé le 04/09/2026 :
          « le relais doit être possible ». Le chauffeur et la remorque ne se
          choisissent pas ici : ils viennent de l'attelage du jour, réglé sur
          l'écran Affectation. Les poser à deux endroits finirait par les faire
          diverger. */}
      {/* L'ÉQUIPAGE DE CETTE ÉTAPE, figé une fois posé. Poser le camion sans
          toucher aux deux autres fait recopier l'attelage du jour par l'API —
          comme choisir un site recopie son adresse. Les corriger ici ne change
          RIEN à l'attelage du parc : ce sont deux choses différentes, et c'est
          ce qui fait qu'un attelage corrigé ne réécrit pas les transports
          passés. */}
      <div className="saisie c2">
        <label htmlFor="etape-vehicule">Camion</label>
        <select
          id="etape-vehicule"
          className="boite"
          value={saisie.vehicule_id}
          onChange={(e) => quandChamp('vehicule_id', e.target.value)}
        >
          <option value="">Pas encore planifiée</option>
          {vehicules
            .filter((v) => v.actif === true || v.id === saisie.vehicule_id)
            .map((v) => (
              <option value={v.id} key={v.id}>
                {texteParc(v, 'immatriculation') ?? v.id}
              </option>
            ))}
        </select>
      </div>
      <div className="saisie c2">
        <label htmlFor="etape-chauffeur">Chauffeur</label>
        <select
          id="etape-chauffeur"
          className="boite"
          value={saisie.chauffeur_id}
          onChange={(e) => quandChamp('chauffeur_id', e.target.value)}
        >
          <option value="">D’après l’attelage</option>
          {chauffeurs
            .filter((c) => c.actif === true || c.id === saisie.chauffeur_id)
            .map((c) => (
              <option value={c.id} key={c.id}>
                {texteParc(c, 'nom') ?? c.id}
              </option>
            ))}
        </select>
      </div>
      <div className="saisie c2">
        <label htmlFor="etape-remorque">Remorque</label>
        <select
          id="etape-remorque"
          className="boite"
          value={saisie.remorque_id}
          onChange={(e) => quandChamp('remorque_id', e.target.value)}
        >
          <option value="">D’après l’attelage</option>
          {remorques
            .filter((r) => r.actif === true || r.id === saisie.remorque_id)
            .map((r) => (
              <option value={r.id} key={r.id}>
                {texteParc(r, 'immatriculation') ?? r.id}
              </option>
            ))}
        </select>
      </div>

      <div className="saisie c6">
        <label htmlFor="etape-note">Note</label>
        <input
          id="etape-note"
          className="boite"
          type="text"
          value={saisie.note}
          onChange={(e) => quandChamp('note', e.target.value)}
        />
      </div>

      {/* Le suivi n'existe pas sur une étape qui n'est pas encore enregistrée :
          on ne coche pas « fait » sur un transport qu'on est en train de saisir,
          et on n'ouvre pas un litige sur une livraison qui n'a pas eu lieu. */}
      {!neuve && (
        <>
          {/* `pleine-en-petit` : sous 640 px la grille n'a que deux colonnes,
              et sans cette règle « Passé le » et « À » — les deux moitiés d'un
              même instant — se retrouvaient sur deux lignes différentes.
              Constaté en REGARDANT l'écran en 420 px le 04/09/2026. */}
          <div className="saisie c2 pleine-en-petit">
            <label htmlFor="etape-fait">Réalisation</label>
            <label className="case-fait" htmlFor="etape-fait">
              <input
                id="etape-fait"
                type="checkbox"
                checked={saisie.fait}
                onChange={(e) => quandChamp('fait', e.target.checked)}
              />
              Validée
            </label>
          </div>
          <div className="saisie c2">
            <label htmlFor="etape-fait-jour">Réalisée le</label>
            <input
              id="etape-fait-jour"
              className="boite"
              type="date"
              disabled={!saisie.fait}
              value={saisie.fait_jour}
              onChange={(e) => quandChamp('fait_jour', e.target.value)}
            />
            {faute('fait_jour')}
          </div>
          <div className="saisie c2">
            <label htmlFor="etape-fait-heure">À</label>
            <input
              id="etape-fait-heure"
              className="boite"
              type="time"
              disabled={!saisie.fait}
              value={saisie.fait_heure}
              onChange={(e) => quandChamp('fait_heure', e.target.value)}
            />
            {faute('fait_heure')}
          </div>
          <p className="aide-saisie c6">
            L’heure réelle de la réalisation se saisit. Le bouton « Valider » de
            l’en-tête la demande aussi, sans passer par ce formulaire.
          </p>

          {/* LE LITIGE A QUITTÉ CE FORMULAIRE — 07/09/2026.
              Il vit dans sa table et se saisit par le bouton « Litige » de
              l'en-tête de l'étape, qui appelle sa propre route. Le laisser ici
              en ferait un champ parmi des heures et des quantités, qu'on pose
              par mégarde en corrigeant autre chose ; et surtout, un formulaire
              d'étape ne peut pas montrer PLUSIEURS litiges successifs, ce que
              la table permet désormais. */}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Les deux boîtes : valider une étape, ouvrir un litige
// ---------------------------------------------------------------------------

/**
 * VALIDER UNE ÉTAPE, et dire QUAND elle a été réalisée.
 *
 * Même forme que la boîte de décrochage de l'écran Affectation, et pour la même
 * raison, apprise le 04/09/2026 sur cet écran-là : poser une date en silence
 * est une faute, et obliger à ouvrir un formulaire pour la saisir en est une
 * autre. On la demande, au moment du geste, avec l'instant courant proposé.
 */
export function BoiteValidation({
  saisie,
  derniere,
  tempsPasse,
  envoi,
  refus,
  quandChamp,
  quandTemps,
  quandFermer,
  quandValider,
}: {
  readonly saisie: SaisieValidation;
  /**
   * LA DERNIÈRE ÉTAPE À FAIRE ? Le temps passé ne se demande qu'alors : c'est
   * le moment où le transport est fini, et le seul où l'on connaît sa durée.
   */
  readonly derniere: boolean;
  readonly tempsPasse: string;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: 'jour' | 'heure', valeur: string) => void;
  readonly quandTemps: (valeur: string) => void;
  readonly quandFermer: () => void;
  readonly quandValider: () => void;
}) {
  const faute = refusValidation(saisie);
  const minutes = minutesSaisies(tempsPasse);
  const tempsFautif = derniere && tempsPasse.trim() !== '' && minutes === null;

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Valider l’étape</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandValider();
          }}
        >
          <div className="deux-boites">
            <div>
              <label className="etiquette-boite" htmlFor="validation-jour">
                Réalisée le
              </label>
              <input
                id="validation-jour"
                className="champ-boite"
                type="date"
                value={saisie.jour}
                disabled={envoi}
                onChange={(e) => quandChamp('jour', e.target.value)}
              />
            </div>
            <div>
              <label className="etiquette-boite" htmlFor="validation-heure">
                à
              </label>
              <input
                id="validation-heure"
                className="champ-boite"
                type="time"
                value={saisie.heure}
                disabled={envoi}
                onChange={(e) => quandChamp('heure', e.target.value)}
              />
            </div>
          </div>
          {/* LE TEMPS PASSÉ — la seule saisie nouvelle du projet, et elle vit
              ICI plutôt que dans un champ à part. Le geste existe déjà et
              demande déjà l'heure ; en ajouter un second serait celui qu'on
              oublie. Décidé le 07/09/2026.

              POURQUOI IL FAUT LE SAISIR : un Orléans → Madrid chargé le 09 à
              7 h et livré le 11 à 8 h donne 49 heures d'écart, avec deux repos
              journaliers dedans. Prendre l'écart tel quel multiplierait le
              coût par trois. */}
          {derniere && (
            <>
              <label className="etiquette-boite" htmlFor="validation-temps">
                Temps passé sur le transport
              </label>
              <input
                id="validation-temps"
                className="champ-boite"
                type="text"
                placeholder="7 h 45"
                value={tempsPasse}
                disabled={envoi}
                onChange={(e) => quandTemps(e.target.value)}
              />
              {tempsFautif ? (
                <p className="bandeau-refus">
                  Écrivez « 7 h 45 », « 7:45 » ou un nombre de minutes.
                </p>
              ) : (
                <p className="aide-boite">
                  {minutes === null
                    ? 'Sans lui, le coût de revient de ce transport ne se calculera pas — l’écart entre le chargement et la livraison porte les repos, et le prendre tel quel triplerait le coût.'
                    : `${minutesDites(minutes) ?? ''} de conduite et de manœuvres. Le coût du transport se calcule dans la foulée.`}
                </p>
              )}
            </>
          )}

          {faute === null ? (
            <p className="aide-boite">
              L’instant courant est proposé. Corrigez-le si l’étape a été réalisée plus
              tôt — le lendemain matin, on valide souvent la veille.
            </p>
          ) : (
            <p className="faute-boite">{faute}</p>
          )}
          {refus !== null && <p className="faute-boite">{refus}</p>}
          <div className="pied-boite">
            <button className="bouton" type="submit" disabled={envoi || faute !== null}>
              {envoi ? 'Enregistrement…' : 'Valider'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * OUVRIR UN LITIGE.
 *
 * **Le motif se CRÉE ici**, et c'est ce qui manquait : la table `motifs_litige`
 * naît vide — la migration n'y insère aucune ligne — et aucun écran n'en
 * créait. Le déroulant ne proposait donc que « Aucun », et le bouton « Litige »
 * ne servait à rien. Constaté par l'utilisateur le 04/09/2026 : « Litige ne
 * marche pas ». C'est le défaut évité sur les absences le 03/09, et refait ici.
 *
 * Les motifs restent PARAMÉTRABLES par société : Stafa est vendu à d'autres
 * transporteurs, qui n'ont pas les mêmes mots.
 */
export function BoiteLitige({
  saisie,
  motifs,
  envoi,
  refus,
  quandChamp,
  quandNouveauMotif,
  quandFermer,
  quandOuvrir,
}: {
  readonly saisie: SaisieLitige;
  readonly motifs: readonly MotifLitige[];
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (
    cle: 'motifId' | 'note' | 'jour' | 'heure',
    valeur: string,
  ) => void;
  /** `null` referme la création et revient au choix dans la liste. */
  readonly quandNouveauMotif: (nom: string | null) => void;
  readonly quandFermer: () => void;
  readonly quandOuvrir: () => void;
}) {
  const faute = refusLitige(saisie);
  const actifs = motifs.filter((m) => m.actif);

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Ouvrir un litige</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandOuvrir();
          }}
        >
          {saisie.motifNouveau === null ? (
            <>
              <label className="etiquette-boite" htmlFor="litige-motif">
                Motif
              </label>
              <select
                id="litige-motif"
                className="champ-boite"
                value={saisie.motifId}
                disabled={envoi}
                onChange={(e) => quandChamp('motifId', e.target.value)}
              >
                <option value="">Choisir un motif…</option>
                {actifs.map((m) => (
                  <option value={m.id} key={m.id}>
                    {m.nom}
                  </option>
                ))}
              </select>
              <div className="pied-creation-site">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  disabled={envoi}
                  onClick={() => quandNouveauMotif('')}
                >
                  + Nouveau motif
                </button>
              </div>
              {actifs.length === 0 && (
                <p className="aide-boite">
                  Aucun motif n’est encore enregistré. Créez le premier : ils sont
                  propres à votre société, et vous les retrouverez ensuite.
                </p>
              )}
            </>
          ) : (
            <>
              <label className="etiquette-boite" htmlFor="litige-nouveau">
                Nouveau motif
              </label>
              <input
                id="litige-nouveau"
                className="champ-boite"
                type="text"
                placeholder="Attente sur site, Marchandise abîmée…"
                value={saisie.motifNouveau}
                disabled={envoi}
                onChange={(e) => quandNouveauMotif(e.target.value)}
              />
              <div className="pied-creation-site">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  disabled={envoi}
                  onClick={() => quandNouveauMotif(null)}
                >
                  Choisir dans la liste
                </button>
              </div>
            </>
          )}

          <label className="etiquette-boite" htmlFor="litige-quoi">
            Ce qui s’est passé
          </label>
          <input
            id="litige-quoi"
            className="champ-boite"
            type="text"
            value={saisie.note}
            disabled={envoi}
            onChange={(e) => quandChamp('note', e.target.value)}
          />

          <div className="deux-boites">
            <div>
              <label className="etiquette-boite" htmlFor="litige-jour">
                Constaté le
              </label>
              <input
                id="litige-jour"
                className="champ-boite"
                type="date"
                value={saisie.jour}
                disabled={envoi}
                onChange={(e) => quandChamp('jour', e.target.value)}
              />
            </div>
            <div>
              <label className="etiquette-boite" htmlFor="litige-heure">
                à
              </label>
              <input
                id="litige-heure"
                className="champ-boite"
                type="time"
                value={saisie.heure}
                disabled={envoi}
                onChange={(e) => quandChamp('heure', e.target.value)}
              />
            </div>
          </div>

          {faute === null ? (
            <p className="aide-boite">
              Le litige restera ouvert tant que personne n’aura écrit comment il a été
              réglé. Cela se fait depuis « Modifier », sur l’étape.
            </p>
          ) : (
            <p className="faute-boite">{faute}</p>
          )}
          {refus !== null && <p className="faute-boite">{refus}</p>}

          <div className="pied-boite">
            <button className="bouton" type="submit" disabled={envoi || faute !== null}>
              {envoi ? 'Enregistrement…' : 'Ouvrir le litige'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Une carte d'étape
// ---------------------------------------------------------------------------


/**
 * LA BOÎTE « ÉCHANGE » — la seule saisie d'emballage de tout le logiciel.
 *
 * Le cadrage §6 : « Échange choisi à la saisie de l'étape, pas sur le site. »
 * L'écran Emballage montre les soldes et tient les bons ; c'est ICI qu'on dit
 * ce qui s'est passé au quai.
 *
 * UNE BOÎTE ET NON DES CHAMPS DANS LE FORMULAIRE DE L'ÉTAPE. Deux raisons :
 * l'échange s'écrit par une AUTRE route (`PUT /emballage/etapes/:id`), et il
 * n'existe qu'une fois l'étape enregistrée — le mêler à la saisie obligerait à
 * deux écritures dans un même geste, dont la seconde pourrait échouer seule.
 * Même choix que la boîte de litige, juste au-dessus.
 *
 * DEUX CHIFFRES SAISIS, ET DEUX SEULEMENT. « Restées » est livrées − reprises,
 * et l'écart aussi : les saisir serait un troisième nombre à tenir d'accord —
 * ce que faisait la v1, avec `restees` ET `ecart` en plus de la quantité.
 */
export function BoiteEchange({
  saisie,
  supports,
  envoi,
  refus,
  quandChamp,
  quandFermer,
  quandEnregistrer,
  quandEffacer,
}: {
  readonly saisie: SaisieEchange;
  readonly supports: readonly LigneReglee[];
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: keyof SaisieEchange, valeur: string) => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
  /** `null` quand il n'y a rien à effacer : l'étape n'a pas encore d'échange. */
  readonly quandEffacer: (() => void) | null;
}) {
  const faute = refusEchange(saisie);
  const ecart = ecartEchange(saisie);
  const echangeables = supports.filter((s) => s.echangeable === true);

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Échange de palettes</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="echange-support">
            Support échangé
          </label>
          <select
            id="echange-support"
            className="champ-boite"
            value={saisie.support}
            disabled={envoi}
            onChange={(e) => quandChamp('support', e.target.value)}
          >
            <option value="">Choisir un support…</option>
            {/* La liste ne propose QUE ce qui se rend. Un support qu'on ne
                reprend pas n'a pas d'échange, et le proposer inviterait à
                saisir un mouvement qui ne veut rien dire. Le support déjà
                saisi reste proposé même s'il a été décoché depuis : on ne
                perd pas une valeur qu'on retrouve dans l'historique. */}
            {supportsAvec(
              saisie.support,
              echangeables.map((s) => s.nom),
            ).map((nom) => (
              <option value={nom} key={nom}>
                {nom}
              </option>
            ))}
          </select>
          {echangeables.length === 0 && (
            <p className="aide-boite">
              Aucun support n’est marqué comme échangeable. Cochez-le dans
              Paramètres → Supports.
            </p>
          )}

          <label className="etiquette-boite" htmlFor="echange-livrees">
            Palettes livrées
          </label>
          <input
            id="echange-livrees"
            className="champ-boite"
            type="text"
            inputMode="numeric"
            value={saisie.livrees}
            disabled={envoi}
            onChange={(e) => quandChamp('livrees', e.target.value)}
          />

          <label className="etiquette-boite" htmlFor="echange-reprises">
            Reprises sur place
          </label>
          <input
            id="echange-reprises"
            className="champ-boite"
            type="text"
            inputMode="numeric"
            value={saisie.reprises}
            disabled={envoi}
            onChange={(e) => quandChamp('reprises', e.target.value)}
          />

          {/* L'ÉCART SE LIT PENDANT QU'ON SAISIT, il ne se saisit jamais.
              C'est lui que le bon reconnaîtra, et c'est lui qui bougera le
              solde du client. */}
          {ecart !== null && ecart !== 0 && (
            <p className="calcule">
              {ecart > 0
                ? `Le client nous devra ${String(ecart)} palettes.`
                : `Nous lui devrons ${String(Math.abs(ecart))} palettes.`}
            </p>
          )}

          <label className="etiquette-boite" htmlFor="echange-bon">
            N° du bon remis par le client
          </label>
          <input
            id="echange-bon"
            className="champ-boite"
            type="text"
            value={saisie.bonNumero}
            disabled={envoi}
            onChange={(e) => quandChamp('bonNumero', e.target.value)}
          />

          {saisie.bonNumero.trim() !== '' && (
            <>
              <label className="etiquette-boite" htmlFor="echange-bon-expire">
                Valable jusqu’au
              </label>
              <input
                id="echange-bon-expire"
                className="champ-boite"
                type="date"
                value={saisie.bonExpire}
                disabled={envoi}
                onChange={(e) => quandChamp('bonExpire', e.target.value)}
              />
              <p className="aide-boite">
                Le bon vient du client, avec son numéro et sa date : on les
                recopie. Stafa ne l’imprime pas.
              </p>
            </>
          )}

          {faute !== null && <p className="erreur-champ">{faute}</p>}
          {refus !== null && <p className="bandeau-refus">{refus}</p>}

          <div className="actions">
            <button
              className="bouton"
              type="submit"
              disabled={envoi || faute !== null}
            >
              {envoi ? 'Enregistrement…' : 'Enregistrer l’échange'}
            </button>
            {quandEffacer !== null && (
              <button
                className="bouton bouton-risque"
                type="button"
                disabled={envoi}
                onClick={quandEffacer}
              >
                Effacer
              </button>
            )}
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


/**
 * LE PANNEAU D'AFFRÈTEMENT — qui appeler, à quel prix, et ce qu'il reste.
 *
 * Cadrage §6 : « Depuis le champ Véhicule : panneau avec transporteur, contact,
 * prix d'achat obligatoire, véhicule demandé, instructions, marge affichée en
 * direct. »
 *
 * L'AXE NE SE SAISIT PAS. Il se déduit du premier chargement et de la dernière
 * livraison — l'API le calcule et le fige à l'enregistrement. Ici, il ne sert
 * qu'à CHERCHER : la liste montre d'abord ceux qui font cet axe.
 *
 * LA MARGE S'AFFICHE EN DIRECT, et elle ne bloque rien. Le cadrage bloque sur
 * le prix de VENTE sous grille, pas sur l'achat : l'exploitation sait des
 * choses que le logiciel ignore. On montre, on n'interdit pas.
 */
export function BoiteAffretement({
  saisie,
  prixVente,
  transporteurs,
  tous,
  chargement,
  envoi,
  refus,
  quandChamp,
  quandNouveau,
  quandFermer,
  quandEnregistrer,
}: {
  readonly saisie: SaisieAffretement;
  readonly prixVente: string | null;
  readonly transporteurs: readonly TransporteurTrouve[];
  /** L'annuaire ENTIER — voir `grouperTransporteurs` pour le pourquoi. */
  readonly tous: readonly LigneListe[];
  readonly chargement: boolean;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: keyof SaisieAffretement, valeur: string) => void;
  readonly quandNouveau: () => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
}) {
  const m = marge(prixVente, saisie.prixAchat === '' ? null : saisie.prixAchat);
  const groupes = grouperTransporteurs(transporteurs, tous);
  // Le téléphone peut venir de l'un OU de l'autre : un transporteur hors axe en
  // a un aussi, et c'est justement lui qu'on va appeler.
  const choisi =
    transporteurs.find((t) => t.id === saisie.transporteurId) ??
    tous.find((t) => t.id === saisie.transporteurId) ??
    null;
  const pret = saisie.transporteurId !== '' && saisie.prixAchat.trim() !== '';

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Affréter cet ordre</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="affretement-qui">
            Transporteur
          </label>
          <select
            id="affretement-qui"
            className="champ-boite"
            value={saisie.transporteurId}
            disabled={envoi || chargement}
            onChange={(e) => {
              if (e.target.value === CHOIX_NOUVEAU) {
                quandNouveau();
                return;
              }
              quandChamp('transporteurId', e.target.value);
            }}
          >
            <option value="">
              {chargement ? 'Recherche des transporteurs…' : 'Choisir…'}
            </option>
            {/* CEUX QUI FONT L'AXE D'ABORD — c'est le moteur qui les a classés :
                nombre de transports, puis dernier affrètement, puis nom. Le
                nombre est écrit à côté, pour qu'on sache POURQUOI celui-là est
                en tête. PUIS TOUS LES AUTRES : la couverture n'est pas toujours
                saisie, et on appelle parfois hors zone (décision du 07/09). */}
            {groupes.axe.length > 0 && (
              <optgroup label="Font cet axe">
                {groupes.axe.map((o) => (
                  <option value={o.id} key={o.id}>
                    {o.texte}
                  </option>
                ))}
              </optgroup>
            )}
            {groupes.autres.length > 0 && (
              <optgroup
                label={groupes.axe.length > 0 ? 'Tous les autres' : 'Tous les transporteurs'}
              >
                {groupes.autres.map((o) => (
                  <option value={o.id} key={o.id}>
                    {o.texte}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Il n’est pas dans la liste">
              <option value={CHOIX_NOUVEAU}>+ Nouveau transporteur…</option>
            </optgroup>
          </select>
          {!chargement && groupes.axe.length === 0 && groupes.autres.length > 0 && (
            <p className="aide-boite">
              Aucun transporteur ne fait cet axe avec ce matériel d’après sa
              couverture. Les autres restent proposés ci-dessus.
            </p>
          )}
          {!chargement && groupes.axe.length === 0 && groupes.autres.length === 0 && (
            <p className="aide-boite">
              Votre annuaire ne contient aucun transporteur actif. Créez-en un
              ci-dessus.
            </p>
          )}
          {choisi?.telephone != null && (
            <p className="calcule">{choisi.telephone}</p>
          )}

          <label className="etiquette-boite" htmlFor="affretement-prix">
            Prix d’achat HT
          </label>
          <input
            id="affretement-prix"
            className="champ-boite"
            type="text"
            inputMode="decimal"
            value={saisie.prixAchat}
            disabled={envoi}
            onChange={(e) => quandChamp('prixAchat', e.target.value)}
          />

          {/* LA MARGE EN DIRECT, dans ses trois états. Le signe et le taux
              portent le sens ; la classe ne fait que le répéter. */}
          {m !== null && (
            <p className={`calcule marge-${m.couleur}`}>{margeDite(m)}</p>
          )}
          {m === null && prixVente === null && (
            <p className="aide-boite">
              Cet ordre n’a pas de prix de vente : la marge ne se calcule pas.
            </p>
          )}

          <label className="etiquette-boite" htmlFor="affretement-contact">
            Contact chez lui
          </label>
          <input
            id="affretement-contact"
            className="champ-boite"
            type="text"
            value={saisie.contactNom}
            disabled={envoi}
            onChange={(e) => quandChamp('contactNom', e.target.value)}
          />

          <label className="etiquette-boite" htmlFor="affretement-instructions">
            Instructions
          </label>
          <input
            id="affretement-instructions"
            className="champ-boite"
            type="text"
            value={saisie.instructions}
            disabled={envoi}
            onChange={(e) => quandChamp('instructions', e.target.value)}
          />

          {refus !== null && <p className="bandeau-refus">{refus}</p>}

          <div className="actions">
            <button className="bouton" type="submit" disabled={envoi || !pret}>
              {envoi ? 'Enregistrement…' : 'Affréter'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * RÉSOUDRE UN LITIGE — dire COMMENT, et quand.
 *
 * La résolution est OBLIGATOIRE : c'est le cadrage, et c'est la base
 * (`litiges_resolution_complete`). Un litige clos sans dire comment ne vaut pas
 * mieux qu'un litige oublié — on le rouvrirait dans six mois sans savoir ce qui
 * avait été convenu.
 *
 * La date se saisit, comme celle d'ouverture : un avoir passé la semaine
 * dernière ne doit pas porter la date d'aujourd'hui.
 */
export function BoiteResolution({
  saisie,
  envoi,
  refus,
  quandChamp,
  quandFermer,
  quandEnregistrer,
}: {
  readonly saisie: {
    readonly texte: string;
    readonly jour: string;
    readonly heure: string;
  };
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: 'texte' | 'jour' | 'heure', valeur: string) => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
}) {
  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Résoudre le litige</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="resolution-texte">
            Comment il a été réglé
          </label>
          <input
            id="resolution-texte"
            className="champ-boite"
            type="text"
            value={saisie.texte}
            disabled={envoi}
            onChange={(e) => quandChamp('texte', e.target.value)}
          />

          <label className="etiquette-boite" htmlFor="resolution-jour">
            Réglé le
          </label>
          <div className="paire-boite">
            <input
              id="resolution-jour"
              className="champ-boite"
              type="date"
              value={saisie.jour}
              disabled={envoi}
              onChange={(e) => quandChamp('jour', e.target.value)}
            />
            <input
              className="champ-boite"
              type="time"
              aria-label="Heure du règlement"
              value={saisie.heure}
              disabled={envoi}
              onChange={(e) => quandChamp('heure', e.target.value)}
            />
          </div>

          <p className="aide-boite">
            Le litige reste dans l’historique de l’étape avec ce texte. Si
            l’incident se répète, on en ouvre un NOUVEAU — celui-ci garde ce qui
            a été convenu.
          </p>

          {refus !== null && <p className="bandeau-refus">{refus}</p>}

          <div className="actions">
            <button
              className="bouton"
              type="submit"
              disabled={envoi || saisie.texte.trim() === ''}
            >
              {envoi ? 'Enregistrement…' : 'Résoudre'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * LE FORMULAIRE DE L'ANNUAIRE, DANS UNE FENÊTRE, PAR-DESSUS L'ORDRE.
 *
 * Demandé le 07/09/2026 : « une fenêtre de l'annuaire mais sans quitter
 * l'ordre ». Ce n'est donc PAS un second formulaire écrit ici — c'est le même
 * `FormulaireFiche` que l'écran Annuaire, avec les mêmes champs, la même
 * validation et le même code proposé. En écrire un second aurait garanti que
 * les deux divergent au premier champ ajouté.
 *
 * Le voile est celui des autres boîtes ; seule la largeur change, parce qu'une
 * fiche de transporteur porte quatorze champs là où une boîte en porte trois.
 */
function BoiteNouveauTransporteur({
  saisie,
  fautes,
  refus,
  envoi,
  quandChamp,
  quandNomQuitte,
  quandEnregistrer,
  quandAnnuler,
}: {
  readonly saisie: Saisie;
  readonly fautes: Readonly<Record<string, string>>;
  readonly refus: string | null;
  readonly envoi: boolean;
  readonly quandChamp: (cle: string, valeur: string) => void;
  readonly quandNomQuitte: () => void;
  readonly quandEnregistrer: () => void;
  readonly quandAnnuler: () => void;
}) {
  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-annuaire" role="dialog" aria-modal="true">
        <FormulaireFiche
          champs={ANNUAIRE_TRANSPORTEURS.champs}
          titre="Nouveau transporteur"
          saisie={saisie}
          fautes={fautes}
          refus={refus}
          envoi={envoi}
          quandChamp={quandChamp}
          quandNomQuitte={quandNomQuitte}
          quandEnregistrer={quandEnregistrer}
          quandAnnuler={quandAnnuler}
        />
      </div>
    </div>
  );
}

function CarteEtape({
  etape,
  edition,
  sites,
  motifs,
  supports,
  vehicules,
  chauffeurs,
  remorques,
  quandModifier,
  quandBasculerFait,
  quandLitige,
  quandResoudre,
  quandEchange,
  quandMonter,
  quandDescendre,
  occupee,
  quandChamp,
  quandSite,
  quandOuvrirSite,
  quandChampSite,
  quandCreerSite,
  quandFermerSite,
  quandEnregistrer,
  quandAnnuler,
  quandDemanderSuppression,
  quandSupprimer,
}: {
  readonly etape: Etape;
  readonly edition: EditionEtape | null;
  readonly sites: readonly LigneListe[];
  readonly motifs: readonly MotifLitige[];
  readonly supports: readonly LigneReglee[];
  readonly vehicules: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  readonly quandModifier: () => void;
  readonly quandBasculerFait: () => void;
  readonly quandLitige: () => void;
  readonly quandResoudre: (litigeId: string) => void;
  readonly quandEchange: () => void;
  readonly quandMonter: (() => void) | null;
  readonly quandDescendre: (() => void) | null;
  readonly occupee: boolean;
  readonly quandChamp: (cle: keyof SaisieEtape, valeur: string | boolean) => void;
  readonly quandSite: (id: string) => void;
  readonly quandOuvrirSite: () => void;
  readonly quandChampSite: (cle: keyof SaisieSite, valeur: string) => void;
  readonly quandCreerSite: () => void;
  readonly quandFermerSite: () => void;
  readonly quandEnregistrer: () => void;
  readonly quandAnnuler: () => void;
  readonly quandDemanderSuppression: () => void;
  readonly quandSupprimer: () => void;
}) {
  const ouverte = edition !== null;
  const classes = ['carte-etape'];
  if (etape.fait) classes.push('faite');
  if (ouverte) classes.push('ouverte');

  const quand = creneau(etape);
  const jour = jourCourt(etape.jour);
  const litige = litigeDit(etape);

  return (
    <div className={classes.join(' ')}>
      <div className="entete-etape">
        <span className="rang">{etape.rang}</span>
        <span className="type-etape">
          {etape.type === 'chargement' ? 'Chargement' : 'Livraison'}
        </span>
        <span className="site-etape">{etape.site_nom}</span>
        {/* « Validée », et non « faite » — mot choisi par l'utilisateur le
            04/09/2026. */}
        {etape.fait && (
          <span className="statut livre">
            {instantDit(etape.fait_le) === null
              ? 'Validée'
              : `Validée le ${instantDit(etape.fait_le) as string}`}
          </span>
        )}
        {/* LES DEUX ACTIONS DU QUOTIDIEN SONT ICI, en un clic.
            Défaut signalé par l'utilisateur le 04/09/2026 : « aucune
            possibilité de valider une étape ou de mettre un litige ». Les deux
            existaient — derrière « Modifier ». C'est exactement le diagnostic
            de l'écran Affectation le 03/09 : l'action la plus fréquente doit
            être la plus visible, pas la mieux cachée. */}
        {!ouverte && (
          <div className="actions-etape">
            {quandMonter !== null && (
              <button
                className="bouton bouton-discret bouton-petit"
                type="button"
                disabled={occupee}
                onClick={quandMonter}
                aria-label={`Monter l’étape ${String(etape.rang)}`}
                title="Monter cette étape"
              >
                ↑
              </button>
            )}
            {quandDescendre !== null && (
              <button
                className="bouton bouton-discret bouton-petit"
                type="button"
                disabled={occupee}
                onClick={quandDescendre}
                aria-label={`Descendre l’étape ${String(etape.rang)}`}
                title="Descendre cette étape"
              >
                ↓
              </button>
            )}
            <button
              className={
                etape.fait
                  ? 'bouton bouton-discret bouton-petit'
                  : 'bouton bouton-petit'
              }
              type="button"
              disabled={occupee}
              onClick={quandBasculerFait}
            >
              {etape.fait ? 'Dévalider' : 'Valider'}
            </button>
            {etape.motif_litige_id === null && (
              <button
                className="bouton bouton-risque bouton-petit"
                type="button"
                disabled={occupee}
                onClick={quandLitige}
              >
                Litige
              </button>
            )}
            {/* L'ÉCHANGE EST UN GESTE DE L'ÉTAPE, comme valider et comme
                ouvrir un litige — et non un champ du formulaire : il s'écrit
                par une autre route, et il n'existe qu'une fois l'étape
                enregistrée. Le libellé dit s'il y en a déjà un, pour qu'on
                sache qu'on va corriger et non créer. */}
            <button
              className="bouton bouton-discret bouton-petit"
              type="button"
              disabled={occupee}
              onClick={quandEchange}
            >
              {etape.echange_support === null ? 'Échange' : 'Échange ✓'}
            </button>
            <button
              className="bouton bouton-discret bouton-petit"
              type="button"
              disabled={occupee}
              onClick={quandModifier}
            >
              Modifier
            </button>
          </div>
        )}
      </div>

      <div className="corps-etape">
        {edition === null ? (
          <dl className="grille-lecture">
            <div className="case-lecture">
              <dt>Adresse du jour</dt>
              <dd>
                {adresseDite(etape).map((ligne, rang) => (
                  <span className="ligne-adresse" key={`${ligne}-${String(rang)}`}>
                    {ligne}
                  </span>
                ))}
              </dd>
            </div>
            <div className="case-lecture">
              <dt>Créneau</dt>
              <dd>
                {jour === null ? (
                  <Absent />
                ) : quand === null ? (
                  jour
                ) : (
                  `${jour} · ${quand}`
                )}
              </dd>
            </div>
            <div className="case-lecture">
              <dt>Quantité</dt>
              <dd>
                {etape.quantite === null ? (
                  <Absent />
                ) : (
                  `${lisible(Number(etape.quantite)) ?? etape.quantite}${
                    etape.support === null ? '' : ` ${etape.support}`
                  }`
                )}
              </dd>
            </div>
            <div className="case-lecture">
              <dt>Poids</dt>
              <dd>
                {etape.poids === null ? (
                  <Absent />
                ) : (
                  `${lisible(etape.poids) as string} kg`
                )}
              </dd>
            </div>
            {/* Revenue le 04/09/2026, une fois le lien posé en base. Elle
                manquait, et l'écran le disait plutôt que d'inventer. */}
            <div className="case-lecture">
              <dt>Ressource affectée</dt>
              <dd>
                {ressourceDite(etape) === null ? (
                  <span className="sourdine">pas encore planifiée</span>
                ) : (
                  <span className="code">{ressourceDite(etape) as string}</span>
                )}
              </dd>
            </div>
            {etape.note !== null && (
              <div className="case-lecture">
                <dt>Note</dt>
                <dd>{etape.note}</dd>
              </div>
            )}
          </dl>
        ) : (
          <>
            <FormulaireEtape
              edition={edition}
              sites={sites}
              motifs={motifs}
              supports={supports}
              vehicules={vehicules}
              chauffeurs={chauffeurs}
              remorques={remorques}
              neuve={false}
              quandChamp={quandChamp}
              quandSite={quandSite}
              quandOuvrirSite={quandOuvrirSite}
              quandChampSite={quandChampSite}
              quandCreerSite={quandCreerSite}
              quandFermerSite={quandFermerSite}
            />
            {edition.refus !== null && <p className="bandeau">{edition.refus}</p>}
          </>
        )}
      </div>

      {/* Le litige est SUR LA CARTE DE SON ÉTAPE, et non dans un onglet à part :
          il faudrait sinon faire l'aller-retour pour savoir de quelle étape on
          parle. Vérifié dans la base v1 : les six incidents y sont tous portés
          par une étape. */}
      {litige !== null && edition === null && (
        <div className="bandeau-litige">
          <span className="motif">{etape.motif_litige ?? 'Litige'}</span>
          {etape.litige_note !== null && <span>{etape.litige_note}</span>}
          {etape.litige_resolution !== null && (
            <span className="sourdine">{etape.litige_resolution}</span>
          )}
          <span className="ou">{litige}</span>
          {/* RÉSOUDRE DEPUIS LA CARTE, ajouté le 07/09/2026 quand le litige a
              quitté le formulaire de l'étape. Sans ce bouton, il faudrait
              passer par l'écran Litiges pour clore ce qu'on vient d'ouvrir. */}
          {etape.litige_id !== null && etape.litige_resolu_le === null && (
            <button
              className="lien-action"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                quandResoudre(etape.litige_id as string);
              }}
            >
              Résoudre
            </button>
          )}
        </div>
      )}

      {edition !== null && (
        <div className="pied-edition">
          <button
            className="bouton"
            type="button"
            disabled={edition.envoi}
            onClick={quandEnregistrer}
          >
            {edition.envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={edition.envoi}
            onClick={quandAnnuler}
          >
            Annuler
          </button>
          {edition.confirme ? (
            <button
              className="bouton bouton-risque a-droite"
              type="button"
              disabled={edition.envoi}
              onClick={quandSupprimer}
            >
              Supprimer définitivement
            </button>
          ) : (
            <button
              className="bouton bouton-risque a-droite"
              type="button"
              disabled={edition.envoi}
              onClick={quandDemanderSuppression}
            >
              Supprimer l’étape
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Le dessin
// ---------------------------------------------------------------------------

export interface ActionsOrdre {
  readonly confirmeAnnulation: boolean;
  readonly confirmeSuppression: boolean;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandAnnulerOrdre: () => void;
  readonly quandRouvrirOrdre: () => void;
  readonly quandSupprimerOrdre: () => void;
  readonly quandDemanderAnnulation: () => void;
  readonly quandDemanderSuppression: () => void;
  readonly quandRenoncer: () => void;
}

// ---------------------------------------------------------------------------
// L'onglet Documents
// ---------------------------------------------------------------------------

/**
 * Les pièces jointes d'un ordre.
 *
 * Une ligne par pièce, et rien de plus : un document n'a pas de fiche à lui.
 * L'état `en_envoi` se voit — c'est une pièce dont le fichier n'est jamais
 * arrivé, et il vaut mieux la montrer que la cacher.
 */
export function OngletDocuments({
  documents,
  types,
  ajout,
  envoi,
  refus,
  quandOuvrirAjout,
  quandChampAjout,
  quandFichier,
  quandJoindre,
  quandFermerAjout,
  quandOuvrirDocument,
  quandSupprimerDocument,
}: {
  readonly documents: readonly DocumentJoint[];
  readonly types: readonly LigneReglee[];
  readonly ajout: AjoutDocument | null;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandOuvrirAjout: () => void;
  readonly quandChampAjout: (typeId: string) => void;
  readonly quandFichier: (fichier: File | null) => void;
  readonly quandJoindre: () => void;
  readonly quandFermerAjout: () => void;
  readonly quandOuvrirDocument: (document: DocumentJoint) => void;
  readonly quandSupprimerDocument: (document: DocumentJoint) => void;
}) {
  const total = documents.reduce((somme, d) => somme + Number(d.poids ?? 0), 0);

  return (
    <div className="documents-ot">
      <div className="barre-documents">
        <span className="compte-documents">
          {documents.length === 0
            ? 'Aucun document'
            : `${documents.length} document${documents.length > 1 ? 's' : ''}${
                total > 0 ? ` · ${poidsDit(total) ?? ''}` : ''
              }`}
        </span>
        <button
          className="bouton"
          type="button"
          disabled={envoi}
          onClick={quandOuvrirAjout}
        >
          Ajouter un document
        </button>
      </div>

      {refus !== null && ajout === null && <p className="bandeau-refus">{refus}</p>}

      {documents.length === 0 ? (
        <p className="rien">
          Aucun document joint à cet ordre. Commande client, lettre de voiture, bon de
          livraison signé, photo…
        </p>
      ) : (
        <table className="table-documents">
          <thead>
            <tr>
              <th>Type</th>
              <th>Fichier</th>
              <th>Poids</th>
              <th>Joint le</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <tr key={document.id}>
                <td>
                  {document.type === null ? (
                    <span className="sourdine">sans type</span>
                  ) : (
                    <span className="type-document">{document.type}</span>
                  )}
                </td>
                <td>
                  {document.nom}
                  {document.etat === 'en_envoi' && (
                    <span className="statut attente"> envoi inachevé</span>
                  )}
                </td>
                <td className="code">{poidsDit(document.poids) ?? '—'}</td>
                <td className="code">
                  {enFrancais(document.cree_le.slice(0, 10)) ?? ''}
                  {document.ajoute_par_nom === null
                    ? ''
                    : ` · ${document.ajoute_par_nom}`}
                </td>
                <td className="actions-document">
                  {document.etat === 'joint' && (
                    <button
                      className="lien-action"
                      type="button"
                      disabled={envoi}
                      onClick={() => quandOuvrirDocument(document)}
                    >
                      Ouvrir
                    </button>
                  )}
                  <button
                    className="lien-action risque"
                    type="button"
                    disabled={envoi}
                    onClick={() => quandSupprimerDocument(document)}
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {ajout !== null && (
        <div className="voile-boite" role="presentation">
          <div className="boite-decrochage" role="dialog" aria-modal="true">
            <h3>Ajouter un document</h3>
            <form
              onSubmit={(evenement) => {
                evenement.preventDefault();
                quandJoindre();
              }}
            >
              <label className="etiquette-boite" htmlFor="document-type">
                Type de document
              </label>
              <select
                id="document-type"
                className="champ-boite"
                value={ajout.typeId}
                disabled={envoi}
                onChange={(evenement) => quandChampAjout(evenement.target.value)}
              >
                <option value="">Sans type</option>
                {types
                  .filter((t) => t.actif)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nom}
                    </option>
                  ))}
              </select>

              <label className="etiquette-boite" htmlFor="document-fichier">
                Fichier
              </label>
              <input
                id="document-fichier"
                className="champ-boite"
                type="file"
                disabled={envoi}
                onChange={(evenement) =>
                  quandFichier(evenement.target.files?.[0] ?? null)
                }
              />
              <p className="aide-boite">20 Mo au plus par fichier.</p>
              {refus !== null && <p className="faute-boite">{refus}</p>}

              <div className="pied-boite">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  disabled={envoi}
                  onClick={quandFermerAjout}
                >
                  Annuler
                </button>
                <button
                  className="bouton"
                  type="submit"
                  disabled={envoi || ajout.fichier === null}
                >
                  {envoi ? 'Envoi en cours…' : 'Joindre'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/** Ce qu'on est en train de joindre, s'il y a quelque chose. */
export interface AjoutDocument {
  readonly typeId: string;
  readonly fichier: File | null;
}

export interface ProprietesVueOrdres {
  /** Les supports réglés par la société — voir `FormulaireEtape`. */
  readonly supports: readonly LigneReglee[];
  readonly documents: readonly DocumentJoint[];
  readonly typesDocument: readonly LigneReglee[];
  readonly ajoutDocument: AjoutDocument | null;
  readonly envoiDocument: boolean;
  readonly refusDocument: string | null;
  readonly quandOuvrirAjout: () => void;
  readonly quandChampAjout: (typeId: string) => void;
  readonly quandFichier: (fichier: File | null) => void;
  readonly quandJoindre: () => void;
  readonly quandFermerAjout: () => void;
  readonly quandOuvrirDocument: (document: DocumentJoint) => void;
  readonly quandSupprimerDocument: (document: DocumentJoint) => void;
  readonly vue: 'liste' | 'fiche' | 'creation';
  readonly liste: EtatListe;
  readonly fiche: EtatFiche;
  readonly onglet: Onglet;
  readonly fenetre: { readonly du: string; readonly au: string };
  /** Combien d'ordres la fenêtre de dates écarte — 0 quand elle n'en cache aucun. */
  readonly horsPeriode: number;
  readonly quandToutesLesDates: () => void;
  readonly statut: string;
  readonly recherche: string;
  readonly clients: readonly LigneListe[];
  readonly sites: readonly LigneListe[];
  readonly gabarits: readonly Vehicule[];
  readonly motifs: readonly MotifLitige[];
  /** Les trois parcs, pour poser un équipage sur une étape. */
  readonly vehicules: readonly LigneParc[];
  readonly chauffeurs: readonly LigneParc[];
  readonly remorques: readonly LigneParc[];
  readonly editionOrdre: EditionOrdre | null;
  readonly editionEtape: EditionEtape | null;
  readonly creation: {
    readonly ordre: EditionOrdre;
    readonly etapes: readonly EditionEtape[];
  } | null;
  readonly actions: ActionsOrdre;
  readonly quandFenetre: (bornes: { readonly du: string; readonly au: string }) => void;
  readonly quandStatut: (statut: string) => void;
  readonly quandRecherche: (texte: string) => void;
  readonly quandOuvrir: (id: string) => void;
  readonly quandRetour: () => void;
  readonly quandOnglet: (onglet: Onglet) => void;
  readonly quandModifierOrdre: () => void;
  readonly quandChampOrdre: (cle: keyof SaisieOrdre, valeur: string) => void;
  readonly quandEnregistrerOrdre: () => void;
  readonly quandAnnulerEdition: () => void;
  readonly quandModifierEtape: (id: string) => void;
  readonly quandBasculerFait: (id: string) => void;
  readonly quandLitige: (id: string) => void;
  /** Résoudre le litige ouvert d'une étape, depuis son bandeau. */
  readonly quandResoudre: (litigeId: string) => void;
  /** La boîte de résolution ouverte, ou `null`. */
  readonly resolution: {
    readonly litigeId: string;
    readonly texte: string;
    readonly jour: string;
    readonly heure: string;
  } | null;
  readonly quandChampResolution: (
    cle: 'texte' | 'jour' | 'heure',
    valeur: string,
  ) => void;
  readonly quandEnregistrerResolution: () => void;
  readonly quandEchange: (id: string) => void;
  /** Le panneau d'affrètement : ouvrir, saisir, enregistrer, rendre. */
  readonly affretement: SaisieAffretement | null;
  readonly proposes: readonly TransporteurTrouve[];
  /** L'annuaire ENTIER des transporteurs, chargé en même temps que la recherche. */
  readonly tousTransporteurs: readonly LigneListe[];
  readonly chercheEnCours: boolean;
  readonly quandAffreter: () => void;
  readonly quandNouveauTransporteur: () => void;
  /** La fiche en cours de création par-dessus le panneau, ou `null`. */
  readonly nouveauTransporteur: {
    readonly saisie: Saisie;
    readonly fautes: Readonly<Record<string, string>>;
    readonly refus: string | null;
  } | null;
  readonly quandChampNouveauTransporteur: (cle: string, valeur: string) => void;
  readonly quandNomQuitteNouveauTransporteur: () => void;
  readonly quandEnregistrerNouveauTransporteur: () => void;
  readonly quandAnnulerNouveauTransporteur: () => void;
  readonly quandChampAffretement: (
    cle: keyof SaisieAffretement,
    valeur: string,
  ) => void;
  readonly quandEnregistrerAffretement: () => void;
  readonly quandAnnulerAffretement: () => void;
  /** La boîte ouverte, ou `null`. Une seule des trois à la fois. */
  readonly validation: SaisieValidation | null;
  readonly litige: SaisieLitige | null;
  /** L'échange en cours de saisie, avec l'étape qu'il concerne. */
  readonly echange: { readonly etapeId: string; readonly saisie: SaisieEchange } | null;
  readonly quandChampValidation: (cle: 'jour' | 'heure', valeur: string) => void;
  /** Le temps passé, saisi dans la boîte « Valider » de la dernière étape. */
  readonly tempsPasse: string;
  readonly quandTemps: (valeur: string) => void;
  /** Le coût de revient de l'ordre ouvert, ou `null` tant qu'il n'est pas lu. */
  readonly cout: CoutOrdre | null;
  readonly quandRecalculerLeCout: () => void;
  readonly quandFermerBoite: () => void;
  readonly quandConfirmerValidation: () => void;
  readonly quandChampLitige: (
    cle: 'motifId' | 'note' | 'jour' | 'heure',
    valeur: string,
  ) => void;
  readonly quandNouveauMotif: (nom: string | null) => void;
  readonly quandOuvrirLitige: () => void;
  readonly quandChampEchange: (cle: keyof SaisieEchange, valeur: string) => void;
  readonly quandEnregistrerEchange: () => void;
  readonly quandEffacerEchange: () => void;
  /** Décale l'étape du rang donné de `pas` places. Renumérote tout l'ordre. */
  readonly quandDeplacerEtape: (rang: number, pas: -1 | 1) => void;
  readonly quandAjouterEtape: (type: TypeEtape) => void;
  readonly quandChampEtape: (cle: keyof SaisieEtape, valeur: string | boolean) => void;
  readonly quandSiteEtape: (id: string) => void;
  readonly quandOuvrirSite: () => void;
  readonly quandChampSite: (cle: keyof SaisieSite, valeur: string) => void;
  readonly quandCreerSite: () => void;
  readonly quandFermerSite: () => void;
  readonly quandEnregistrerEtape: () => void;
  readonly quandAnnulerEtape: () => void;
  readonly quandDemanderSuppressionEtape: () => void;
  readonly quandSupprimerEtape: () => void;
  readonly quandCreer: () => void;
  readonly quandChampEtapeCreation: (
    rang: number,
    cle: keyof SaisieEtape,
    valeur: string | boolean,
  ) => void;
  readonly quandSiteCreation: (rang: number, id: string) => void;
  readonly quandOuvrirSiteCreation: (rang: number) => void;
  readonly quandChampSiteCreation: (
    rang: number,
    cle: keyof SaisieSite,
    valeur: string,
  ) => void;
  readonly quandCreerSiteCreation: (rang: number) => void;
  readonly quandFermerSiteCreation: (rang: number) => void;
  readonly quandAjouterEtapeCreation: (type: TypeEtape) => void;
  readonly quandRetirerEtapeCreation: (rang: number) => void;
  /** À la création, les étapes n'existent pas en base : on déplace la liste. */
  readonly quandDeplacerEtapeCreation: (rang: number, pas: -1 | 1) => void;
  readonly quandEnregistrerCreation: () => void;
}

/** Le dessin, et rien d'autre : aucun appel réseau, aucun état gardé. */
export function VueOrdres(p: ProprietesVueOrdres) {
  if (p.vue === 'creation' && p.creation !== null) {
    return <VueCreation p={p} creation={p.creation} />;
  }
  if (p.vue === 'fiche') {
    return <VueFiche p={p} />;
  }
  return <VueListe p={p} />;
}

// ---------------------------------------------------------------------------

function VueListe({ p }: { readonly p: ProprietesVueOrdres }) {
  const ordres = p.liste.nom === 'prete' ? ranger(p.liste.ordres) : [];
  const visibles = filtrer(ordres, p.recherche);

  return (
    <div className="corps">
      <section className="liste">
        <div className="barre-outils">
          <input
            className="recherche"
            type="search"
            placeholder="N° d’OT, client, référence, ville…"
            aria-label="Rechercher un ordre de transport"
            value={p.recherche}
            onChange={(e) => p.quandRecherche(e.target.value)}
          />
          {/* Les mêmes classes que la barre de l'écran Affectation : `champ-date`
              tient l'étiquette et son champ ensemble, et `champ` donne au champ
              la taille des autres. */}
          <label className="champ-date">
            <span className="etiquette">Du</span>
            <input
              className="champ"
              type="date"
              value={p.fenetre.du}
              onChange={(e) => p.quandFenetre({ ...p.fenetre, du: e.target.value })}
            />
          </label>
          <label className="champ-date">
            <span className="etiquette">au</span>
            <input
              className="champ"
              type="date"
              value={p.fenetre.au}
              onChange={(e) => p.quandFenetre({ ...p.fenetre, au: e.target.value })}
            />
          </label>
          <label className="champ-date">
            <span className="etiquette">Statut</span>
            <select
              className="champ"
              value={p.statut}
              onChange={(e) => p.quandStatut(e.target.value)}
            >
              <option value="">Tous</option>
              {STATUTS.map((s) => (
                <option value={s.cle} key={s.cle}>
                  {s.nom}
                </option>
              ))}
            </select>
          </label>
          <button className="bouton" type="button" onClick={p.quandCreer}>
            Nouvel OT
          </button>
        </div>

        {visibles.length > 0 && (
          <p className="decompte">
            {decompte(visibles)}
            {p.horsPeriode > 0 && (
              <>
                {' — '}
                <button
                  className="lien-action"
                  type="button"
                  onClick={p.quandToutesLesDates}
                >
                  {p.horsPeriode === 1
                    ? '1 ordre hors de cette période'
                    : `${p.horsPeriode} ordres hors de cette période`}
                </button>
              </>
            )}
          </p>
        )}

        {p.liste.nom === 'chargement' && <p className="attente">Chargement…</p>}

        {p.liste.nom === 'panne' && (
          <div className="vide">
            <p>Stafa ne répond pas. Vérifiez votre connexion et réessayez.</p>
          </div>
        )}

        {p.liste.nom === 'prete' && ordres.length === 0 && (
          <div className="vide">
            <p>
              {p.fenetre.du === '' && p.fenetre.au === ''
                ? 'Aucun ordre de transport.'
                : `Aucun ordre de transport entre le ${
                    enFrancais(p.fenetre.du) ?? p.fenetre.du
                  } et le ${enFrancais(p.fenetre.au) ?? p.fenetre.au}.`}
            </p>
            {/* Ce qui manquait le 04/09/2026 : la liste cachait un ordre daté
                du mois d'avant et n'en disait rien. */}
            {p.horsPeriode > 0 ? (
              <>
                <p className="sourdine">
                  {p.horsPeriode === 1
                    ? '1 ordre est hors de cette période.'
                    : `${p.horsPeriode} ordres sont hors de cette période.`}
                </p>
                <button
                  className="bouton"
                  type="button"
                  onClick={p.quandToutesLesDates}
                >
                  Voir toutes les dates
                </button>
              </>
            ) : (
              <button className="bouton" type="button" onClick={p.quandCreer}>
                Créer un ordre de transport
              </button>
            )}
          </div>
        )}

        {p.liste.nom === 'prete' && ordres.length > 0 && visibles.length === 0 && (
          <div className="vide">
            <p>Aucun ordre ne correspond à « {p.recherche.trim()} ».</p>
            <button
              className="bouton bouton-discret"
              type="button"
              onClick={() => p.quandRecherche('')}
            >
              Effacer la recherche
            </button>
          </div>
        )}

        {visibles.length > 0 && (
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  <th scope="col">N° d’OT</th>
                  <th scope="col" className="col-souple">
                    Client
                  </th>
                  <th scope="col">Chargement</th>
                  <th scope="col" className="col-large col-souple">
                    Adresse de chargement
                  </th>
                  <th scope="col">Livraison</th>
                  <th scope="col" className="col-large col-souple">
                    Adresse de livraison
                  </th>
                  <th scope="col">Statut</th>
                  {/* `col-large` : elle disparaît sous 760 px, comme les deux
                      adresses. MESURÉ le 04/09/2026 — sans elle, le tableau
                      débordait de 32 px de son cadre à cette largeur.

                      La colonne Gabarit a été RETIRÉE le 05/09/2026 pour faire
                      la place aux deux adresses. Mesuré sur la maquette :
                      1406 px à 1440 px de fenêtre, pas de débord. Le gabarit
                      reste lisible sur la fiche de l'ordre. */}
                  <th scope="col" className="prix col-large">
                    Vente HT
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((o) => {
                  const charge = chargementDit(o);
                  const livre = livraisonDit(o);
                  return (
                    <tr
                      className="rangee"
                      key={o.id}
                      tabIndex={0}
                      onClick={() => p.quandOuvrir(o.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          p.quandOuvrir(o.id);
                        }
                      }}
                    >
                      <td className="code">{o.numero}</td>
                      <td className="col-souple">
                        {o.client}
                        {o.reference_client !== null && (
                          <span className="secondaire">{o.reference_client}</span>
                        )}
                      </td>
                      <td>
                        {charge.jour ?? <Absent />}
                        {charge.heure !== null && (
                          <span className="secondaire">{charge.heure}</span>
                        )}
                      </td>
                      <td className="col-large col-souple">
                        {charge.site ?? <Absent />}
                        {charge.localite !== null && (
                          <span className="secondaire">{charge.localite}</span>
                        )}
                      </td>
                      <td>
                        {livre.jour ?? <Absent />}
                        {livre.heure !== null && (
                          <span className="secondaire">{livre.heure}</span>
                        )}
                      </td>
                      <td className="col-large col-souple">
                        {livre.site ?? <Absent />}
                        {livre.localite !== null && (
                          <span className="secondaire">{livre.localite}</span>
                        )}
                      </td>
                      <td>
                        <Statut statut={o.statut} />
                        {/* LA PASTILLE « AFFRÉTÉ », remise le 07/09/2026.
                            La colonne « Traité par » qui portait cette
                            information a sauté le 06/09 pour faire place aux
                            adresses de chargement et de livraison, et plus rien
                            ne disait qu'un ordre était sous-traité — constaté à
                            l'essai. Une pastille plutôt qu'une colonne : le
                            tableau tient déjà tout juste en 1440 px. Le nom du
                            transporteur et le prix d'achat sont dans
                            l'infobulle, où ils ne coûtent aucune largeur. */}
                        {estAffrete(o) && (
                          <span
                            className="statut affrete"
                            title={affreteDit(o) ?? undefined}
                          >
                            Affrété
                          </span>
                        )}
                        {o.litiges_ouverts > 0 && (
                          <span className="statut litige">
                            {o.litiges_ouverts > 1
                              ? `${o.litiges_ouverts} litiges`
                              : '1 litige'}
                          </span>
                        )}
                      </td>
                      <td className="prix col-large">{enEuros(o.prix_vente) ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function VueFiche({ p }: { readonly p: ProprietesVueOrdres }) {
  if (p.fiche.nom === 'chargement') {
    return (
      <div className="corps corps-ot">
        <p className="attente">Chargement…</p>
      </div>
    );
  }

  if (p.fiche.nom !== 'prete') {
    return (
      <div className="corps corps-ot">
        <div className="bandeau-fiche">
          <button className="retour" type="button" onClick={p.quandRetour}>
            ← Les OT
          </button>
        </div>
        <div className="vide">
          <p>
            {p.fiche.nom === 'panne' ? REFUS[p.fiche.cause] : 'Aucun ordre ouvert.'}
          </p>
        </div>
      </div>
    );
  }

  const { ordre_transport: ordre, etapes } = p.fiche.fiche;
  const t = totaux(etapes);

  return (
    <div className="corps corps-ot">
      {/* Les deux boîtes passent PAR-DESSUS la fiche : on valide ou on ouvre un
          litige en un geste, sans ouvrir le formulaire de l'étape. */}
      {p.validation !== null && (
        <BoiteValidation
          saisie={p.validation}
          {...(() => {
            // LA DERNIÈRE À FAIRE : toutes les autres sont déjà validées.
            // C'est alors que le transport est fini, et que sa durée se sait.
            const restantes = etapes.filter(
              (e) => !e.fait && e.id !== p.validation?.etapeId,
            );
            return { derniere: restantes.length === 0 };
          })()}
          tempsPasse={p.tempsPasse}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampValidation}
          quandTemps={p.quandTemps}
          quandFermer={p.quandFermerBoite}
          quandValider={p.quandConfirmerValidation}
        />
      )}
      {p.affretement !== null && (
        <BoiteAffretement
          saisie={p.affretement}
          prixVente={ordre.prix_vente}
          transporteurs={p.proposes}
          tous={p.tousTransporteurs}
          chargement={p.chercheEnCours}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampAffretement}
          quandNouveau={p.quandNouveauTransporteur}
          quandFermer={p.quandFermerBoite}
          quandEnregistrer={p.quandEnregistrerAffretement}
        />
      )}
      {p.resolution !== null && (
        <BoiteResolution
          saisie={p.resolution}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampResolution}
          quandFermer={p.quandFermerBoite}
          quandEnregistrer={p.quandEnregistrerResolution}
        />
      )}
      {/* PAR-DESSUS le panneau, qui reste ouvert dessous : le prix et le
          contact déjà tapés ne sont pas perdus. */}
      {p.nouveauTransporteur !== null && (
        <BoiteNouveauTransporteur
          saisie={p.nouveauTransporteur.saisie}
          fautes={p.nouveauTransporteur.fautes}
          refus={p.nouveauTransporteur.refus ?? p.actions.refus}
          envoi={p.actions.envoi}
          quandChamp={p.quandChampNouveauTransporteur}
          quandNomQuitte={p.quandNomQuitteNouveauTransporteur}
          quandEnregistrer={p.quandEnregistrerNouveauTransporteur}
          quandAnnuler={p.quandAnnulerNouveauTransporteur}
        />
      )}
      {p.echange !== null && (
        <BoiteEchange
          saisie={p.echange.saisie}
          supports={p.supports}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampEchange}
          quandFermer={p.quandFermerBoite}
          quandEnregistrer={p.quandEnregistrerEchange}
          quandEffacer={
            etapes.find((e) => e.id === p.echange?.etapeId)?.echange_support ===
            undefined
              ? null
              : etapes.find((e) => e.id === p.echange?.etapeId)
                    ?.echange_support === null
                ? null
                : p.quandEffacerEchange
          }
        />
      )}
      {p.litige !== null && (
        <BoiteLitige
          saisie={p.litige}
          motifs={p.motifs}
          envoi={p.actions.envoi}
          refus={p.actions.refus}
          quandChamp={p.quandChampLitige}
          quandNouveauMotif={p.quandNouveauMotif}
          quandFermer={p.quandFermerBoite}
          quandOuvrir={p.quandOuvrirLitige}
        />
      )}
      <div className="bandeau-fiche">
        <button className="retour" type="button" onClick={p.quandRetour}>
          ← Les OT
        </button>
        <div className="titre-fiche">
          <div className="ligne">
            <h3>{ordre.numero}</h3>
            <Statut statut={ordre.statut} />
            {ordre.litiges_ouverts > 0 && (
              <span className="statut litige">
                {ordre.litiges_ouverts > 1
                  ? `${ordre.litiges_ouverts} litiges`
                  : '1 litige'}
              </span>
            )}
          </div>
          <span className="client">
            {ordre.client}
            {ordre.reference_client !== null &&
              ` · référence ${ordre.reference_client}`}
          </span>
          {/* CE QUI A REMPLACÉ LE MODULE AFFRÈTEMENT le 05/09/2026 : l'ordre ne
              change pas de liste, c'est une ligne qui apparaît. La marge se
              calcule des deux prix figés — elle n'est stockée nulle part. */}
          {ordre.affrete_par !== null && (
            <span className="client">
              Affrété · {ordre.affrete_par}
              {(() => {
                const m = marge(ordre.prix_vente, ordre.prix_achat);
                return m === null ? null : (
                  <span className={`marge-${m.couleur}`}> · {margeDite(m)}</span>
                );
              })()}
            </span>
          )}
        </div>
        <div className="actions-fiche">
          {p.editionOrdre === null && (
            <>
              <button className="bouton" type="button" onClick={p.quandModifierOrdre}>
                Modifier
              </button>
              {/* AFFRÉTER OU RENDRE. Un ordre annulé ne s'affrète pas — l'API
                  le refuse aussi, deux barrières comme partout. */}
              {ordre.statut !== 'annule' && ordre.affretement_id === null && (
                <button
                  className="bouton bouton-discret"
                  type="button"
                  disabled={p.actions.envoi}
                  onClick={p.quandAffreter}
                >
                  Affréter
                </button>
              )}
              {ordre.affretement_id !== null && (
                <button
                  className="bouton bouton-risque"
                  type="button"
                  disabled={p.actions.envoi}
                  onClick={p.quandAnnulerAffretement}
                >
                  Rendre à l’exploitation
                </button>
              )}
              {/* Les deux SEULES décisions humaines sur le statut. Les quatre
                  autres valeurs se déduisent des étapes, et c'est pourquoi le
                  formulaire de modification n'en propose plus aucune.

                  Rouvrir ne demande pas confirmation : il ne détruit rien, et
                  l'API rend l'état réel des étapes — un ordre annulé dont tout
                  était livré revient à « Livré », pas à « En attente ». */}
              {ordre.statut !== 'annule' ? (
                <button
                  className="bouton bouton-risque"
                  type="button"
                  disabled={p.actions.envoi}
                  onClick={p.actions.quandDemanderAnnulation}
                >
                  Annuler l’OT
                </button>
              ) : (
                <>
                  <button
                    className="bouton"
                    type="button"
                    disabled={p.actions.envoi}
                    onClick={p.actions.quandRouvrirOrdre}
                  >
                    {p.actions.envoi ? 'Réouverture…' : 'Rouvrir'}
                  </button>
                  <button
                    className="bouton bouton-risque"
                    type="button"
                    disabled={p.actions.envoi}
                    onClick={p.actions.quandDemanderSuppression}
                  >
                    Supprimer
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {p.actions.refus !== null && <p className="bandeau">{p.actions.refus}</p>}

      {/* Annuler garde l'ordre dans l'histoire ; supprimer l'efface AVEC SES
          ÉTAPES et ne se propose qu'une fois l'ordre annulé — même règle que
          « désactiver puis supprimer » ailleurs. */}
      {p.actions.confirmeAnnulation && (
        <div className="confirmation">
          <p className="empechement">
            Passer <strong>{ordre.numero}</strong> en « Annulé » ? Il restera
            consultable et dans les états, comme les transports annulés de l’ancien
            logiciel.
          </p>
          <div className="actions">
            <button
              className="bouton bouton-risque"
              type="button"
              disabled={p.actions.envoi}
              onClick={p.actions.quandAnnulerOrdre}
            >
              {p.actions.envoi ? 'Annulation…' : 'Annuler l’OT'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={p.actions.envoi}
              onClick={p.actions.quandRenoncer}
            >
              Ne rien faire
            </button>
          </div>
        </div>
      )}

      {p.actions.confirmeSuppression && (
        <div className="confirmation">
          <p className="empechement">
            Supprimer définitivement <strong>{ordre.numero}</strong> ?{' '}
            <strong>Ses {ordre.etapes} étapes seront effacées avec lui.</strong> À ne
            faire que pour une saisie fautive : un transport qui n’a pas eu lieu
            s’annule. Cette action est sans retour.
          </p>
          <div className="actions">
            <button
              className="bouton bouton-risque"
              type="button"
              disabled={p.actions.envoi}
              onClick={p.actions.quandSupprimerOrdre}
            >
              {p.actions.envoi ? 'Suppression…' : 'Supprimer définitivement'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={p.actions.envoi}
              onClick={p.actions.quandRenoncer}
            >
              Ne rien faire
            </button>
          </div>
        </div>
      )}

      <div className="onglets">
        <button
          className="onglet-fiche"
          type="button"
          aria-selected={p.onglet === 'commande'}
          onClick={() => p.quandOnglet('commande')}
        >
          Commande
        </button>
        <button
          className="onglet-fiche"
          type="button"
          aria-selected={p.onglet === 'etapes'}
          onClick={() => p.quandOnglet('etapes')}
        >
          Étapes <span className="compteur">{etapes.length}</span>
        </button>
        <button
          className="onglet-fiche"
          type="button"
          aria-selected={p.onglet === 'documents'}
          onClick={() => p.quandOnglet('documents')}
        >
          Documents
          {p.documents.length > 0 && (
            <span className="compteur">{p.documents.length}</span>
          )}
        </button>
      </div>

      <div className="contenu-onglet">
        {p.onglet === 'documents' ? (
          <OngletDocuments
            documents={p.documents}
            types={p.typesDocument}
            ajout={p.ajoutDocument}
            envoi={p.envoiDocument}
            refus={p.refusDocument}
            quandOuvrirAjout={p.quandOuvrirAjout}
            quandChampAjout={p.quandChampAjout}
            quandFichier={p.quandFichier}
            quandJoindre={p.quandJoindre}
            quandFermerAjout={p.quandFermerAjout}
            quandOuvrirDocument={p.quandOuvrirDocument}
            quandSupprimerDocument={p.quandSupprimerDocument}
          />
        ) : p.onglet === 'commande' ? (
          p.editionOrdre === null ? (
            <OngletCommande
              ordre={ordre}
              etapes={etapes}
              totaux={t}
              cout={p.cout}
              actions={p.actions}
              quandRecalculerLeCout={p.quandRecalculerLeCout}
            />
          ) : (
            <FormulaireOrdre
              edition={p.editionOrdre}
              clients={p.clients}
              gabarits={p.gabarits}
              quandChamp={p.quandChampOrdre}
              quandEnregistrer={p.quandEnregistrerOrdre}
              quandAnnuler={p.quandAnnulerEdition}
            />
          )
        ) : (
          <div className="cartes-etapes">
            {etapes.length === 0 && (
              <p className="rien">
                Aucune étape. Un ordre sans étape n’a ni date ni trajet : ajoutez-en
                une.
              </p>
            )}
            {etapes.map((e, rang) => (
              <CarteEtape
                key={e.id}
                etape={e}
                supports={p.supports}
                edition={
                  p.editionEtape !== null && p.editionEtape.id === e.id
                    ? p.editionEtape
                    : null
                }
                sites={p.sites}
                motifs={p.motifs}
                vehicules={p.vehicules}
                chauffeurs={p.chauffeurs}
                remorques={p.remorques}
                quandModifier={() => p.quandModifierEtape(e.id)}
                quandBasculerFait={() => p.quandBasculerFait(e.id)}
                quandLitige={() => p.quandLitige(e.id)}
                quandResoudre={(litigeId) => p.quandResoudre(litigeId)}
                quandEchange={() => p.quandEchange(e.id)}
                quandMonter={rang === 0 ? null : () => p.quandDeplacerEtape(rang, -1)}
                quandDescendre={
                  rang === etapes.length - 1
                    ? null
                    : () => p.quandDeplacerEtape(rang, 1)
                }
                occupee={p.actions.envoi}
                quandChamp={p.quandChampEtape}
                quandSite={p.quandSiteEtape}
                quandOuvrirSite={p.quandOuvrirSite}
                quandChampSite={p.quandChampSite}
                quandCreerSite={p.quandCreerSite}
                quandFermerSite={p.quandFermerSite}
                quandEnregistrer={p.quandEnregistrerEtape}
                quandAnnuler={p.quandAnnulerEtape}
                quandDemanderSuppression={p.quandDemanderSuppressionEtape}
                quandSupprimer={p.quandSupprimerEtape}
              />
            ))}

            {/* L'étape qu'on ajoute : même formulaire, dans une carte sans
                numéro — le rang est déduit par l'API, l'écran n'a pas à
                compter. */}
            {p.editionEtape !== null && p.editionEtape.id === null && (
              <div className="carte-etape ouverte">
                <div className="entete-etape">
                  <span className="rang">+</span>
                  <span className="type-etape">
                    {p.editionEtape.saisie.type === 'chargement'
                      ? 'Chargement'
                      : 'Livraison'}
                  </span>
                  <span className="site-etape">Nouvelle étape</span>
                </div>
                <div className="corps-etape">
                  <FormulaireEtape
                    edition={p.editionEtape}
                    sites={p.sites}
                    motifs={p.motifs}
                    supports={p.supports}
                    vehicules={p.vehicules}
                    chauffeurs={p.chauffeurs}
                    remorques={p.remorques}
                    neuve
                    quandChamp={p.quandChampEtape}
                    quandSite={p.quandSiteEtape}
                    quandOuvrirSite={p.quandOuvrirSite}
                    quandChampSite={p.quandChampSite}
                    quandCreerSite={p.quandCreerSite}
                    quandFermerSite={p.quandFermerSite}
                  />
                  {p.editionEtape.refus !== null && (
                    <p className="bandeau">{p.editionEtape.refus}</p>
                  )}
                </div>
                <div className="pied-edition">
                  <button
                    className="bouton"
                    type="button"
                    disabled={p.editionEtape.envoi}
                    onClick={p.quandEnregistrerEtape}
                  >
                    {p.editionEtape.envoi ? 'Enregistrement…' : 'Ajouter l’étape'}
                  </button>
                  <button
                    className="bouton bouton-discret"
                    type="button"
                    disabled={p.editionEtape.envoi}
                    onClick={p.quandAnnulerEtape}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {p.editionEtape === null && (
              <div className="ajouter-etape">
                <button
                  className="bouton bouton-discret"
                  type="button"
                  onClick={() => p.quandAjouterEtape('chargement')}
                >
                  + Un chargement
                </button>
                <button
                  className="bouton bouton-discret"
                  type="button"
                  onClick={() => p.quandAjouterEtape('livraison')}
                >
                  + Une livraison
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** La somme des trois natures. Elle ne se stocke pas : voir la migration. */
function totalDuCout(c: CoutOrdre): number {
  if (c.fige === null) return 0;
  return (
    Number(c.fige.cout_temps) + Number(c.fige.cout_km) + Number(c.fige.cout_peage)
  );
}

function OngletCommande({
  ordre,
  etapes,
  totaux: t,
  cout,
  actions,
  quandRecalculerLeCout,
}: {
  readonly ordre: OrdreTransport;
  readonly etapes: readonly Etape[];
  readonly totaux: {
    readonly quantite: number | null;
    readonly support: string | null;
    readonly poids: number | null;
  };
  readonly cout: CoutOrdre | null;
  readonly actions: ActionsOrdre;
  readonly quandRecalculerLeCout: () => void;
}) {
  const chargement = etapes.find((e) => e.type === 'chargement');
  const livraison = [...etapes].reverse().find((e) => e.type === 'livraison');

  const quandOu = (e: Etape | undefined) => {
    if (e === undefined) return null;
    const jour = enFrancais(e.jour);
    const heures = creneau(e);
    return `${jour ?? e.jour}${heures === null ? '' : ` · ${heures}`}`;
  };

  const alaPalette = rapport(ordre.prix_vente, t.quantite);
  const auKilometre = rapport(ordre.prix_vente, ordre.distance_km);

  return (
    <div className="groupes">
      <div className="groupe">
        <p className="titre-groupe">Le client</p>
        <dl className="champs-ot">
          <Champ nom="Client">{ordre.client}</Champ>
          <Champ nom="Référence client">{ordre.reference_client ?? <Absent />}</Champ>
          <Champ nom="N° d’OT" calcule="attribué à la création">
            <span className="code">{ordre.numero}</span>
          </Champ>
          {/* « par qui » n'est pas affiché : l'auteur n'est pas sur l'ordre, il
              n'existe que dans le journal des traces. L'inventer serait pire
              que de le taire. */}
          <Champ nom="Saisi le">{instantDit(ordre.cree_le) ?? <Absent />}</Champ>
        </dl>
      </div>

      <div className="groupe">
        <p className="titre-groupe">Le transport</p>
        <dl className="champs-ot">
          <Champ nom="Chargement" calcule={chargement?.site_ville ?? undefined}>
            {quandOu(chargement) ?? <Absent />}
          </Champ>
          <Champ nom="Livraison" calcule={livraison?.site_ville ?? undefined}>
            {quandOu(livraison) ?? <Absent />}
          </Champ>
          <Champ nom="Gabarit demandé">{ordre.gabarit ?? <Absent />}</Champ>
          <Champ nom="Distance">
            {ordre.distance_km === null ? (
              <Absent />
            ) : (
              <span className="code">{lisible(ordre.distance_km) as string} km</span>
            )}
          </Champ>
        </dl>
      </div>

      <div className="groupe">
        <p className="titre-groupe">La marchandise</p>
        <dl className="champs-ot">
          <Champ nom="Nature">{ordre.marchandise ?? <Absent />}</Champ>
          <Champ nom="Total chargé" calcule="somme des chargements">
            {t.quantite === null ? (
              <Absent />
            ) : (
              `${lisible(t.quantite) as string}${t.support === null ? '' : ` ${t.support}`}`
            )}
          </Champ>
          <Champ nom="Poids total" calcule="somme des chargements">
            {t.poids === null ? <Absent /> : `${lisible(t.poids) as string} kg`}
          </Champ>
        </dl>
      </div>

      <div className="groupe">
        <p className="titre-groupe">Le prix</p>
        <dl className="champs-ot">
          <Champ nom="Vente HT">
            {ordre.prix_vente === null ? (
              <Absent />
            ) : (
              <span className="grand">{enEuros(ordre.prix_vente) as string}</span>
            )}
          </Champ>
          <Champ nom="À la palette" calcule="calculé">
            {alaPalette === null ? (
              <Absent />
            ) : (
              <span className="code">{alaPalette} €</span>
            )}
          </Champ>
          <Champ nom="Au kilomètre" calcule="calculé">
            {auKilometre === null ? (
              <Absent />
            ) : (
              <span className="code">{auKilometre} €/km</span>
            )}
          </Champ>
        </dl>
      </div>

      {/* LE COÛT DE REVIENT, lot 2 du 07/09/2026.
          Il est FIGÉ : ce qui est écrit là est ce que le transport a coûté au
          moment du calcul, avec les prix de ce moment-là. Corriger un loyer
          aujourd'hui ne le réécrit pas — c'est la règle du §6. */}
      <div className="groupe">
        <p className="titre-groupe">Le coût de revient</p>
        {cout === null ? (
          <p className="sourdine">Chargement…</p>
        ) : cout.fige === null ? (
          <>
            <p className="sourdine">
              {manquesDits([...cout.calcule.manques]) ??
                'Le coût n’a pas encore été calculé.'}
            </p>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={actions.envoi}
              onClick={quandRecalculerLeCout}
            >
              Recalculer
            </button>
          </>
        ) : (
          <>
            <dl className="champs-ot">
              <Champ nom="Temps et ressources" calcule="figé">
                <span className="code">
                  {enEuros(cout.fige.cout_temps) ?? '—'}
                </span>
              </Champ>
              <Champ nom="Kilométrique" calcule="figé">
                <span className="code">{enEuros(cout.fige.cout_km) ?? '—'}</span>
              </Champ>
              <Champ nom="Péage" calcule="figé">
                <span className="code">{enEuros(cout.fige.cout_peage) ?? '—'}</span>
              </Champ>
              <Champ nom="Coût total" calcule="somme des trois">
                <span className="grand">{enEuros(String(totalDuCout(cout))) ?? '—'}</span>
              </Champ>
              {/* LA MARGE RÉELLE : la vente moins ce que NOS moyens ont coûté.
                  Distincte de la marge d'affrètement, qui compare au prix d'un
                  sous-traitant — les confondre ferait croire qu'un transport
                  fait en propre n'a pas de coût. */}
              <Champ nom="Marge réelle" calcule="calculée">
                {(() => {
                  const m = margeReelle(ordre.prix_vente, totalDuCout(cout));
                  return m === null ? (
                    <Absent />
                  ) : (
                    <span className={`grand marge-${m.couleur}`}>
                      {margeReelleDite(m)}
                    </span>
                  );
                })()}
              </Champ>
              <Champ nom="Temps passé" calcule="saisi">
                {minutesDites(cout.temps_reel_minutes) ?? <Absent />}
              </Champ>
            </dl>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={actions.envoi}
              onClick={quandRecalculerLeCout}
            >
              Recalculer
            </button>
          </>
        )}
      </div>

      {ordre.note !== null && (
        <div className="groupe pleine">
          <p className="titre-groupe">Note</p>
          <p className="note-ot">{ordre.note}</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function FormulaireOrdre({
  edition,
  clients,
  gabarits,
  quandChamp,
  quandEnregistrer,
  quandAnnuler,
}: {
  readonly edition: EditionOrdre;
  readonly clients: readonly LigneListe[];
  readonly gabarits: readonly Vehicule[];
  readonly quandChamp: (cle: keyof SaisieOrdre, valeur: string) => void;
  readonly quandEnregistrer?: () => void;
  readonly quandAnnuler?: () => void;
}) {
  const { saisie, fautes } = edition;
  const faute = (cle: string) =>
    fautes[cle] === undefined ? null : (
      <span className="erreur-champ">{fautes[cle]}</span>
    );

  return (
    <div className="carte-formulaire">
      <div className="grille-saisie">
        <div className="saisie c2">
          <label htmlFor="ot-client">Client *</label>
          <select
            id="ot-client"
            className="boite"
            value={saisie.client_id}
            onChange={(e) => quandChamp('client_id', e.target.value)}
          >
            <option value="">Choisir un client…</option>
            {clients
              .filter((c) => c.actif || c.id === saisie.client_id)
              .map((c) => (
                <option value={c.id} key={c.id}>
                  {c.nom}
                </option>
              ))}
          </select>
          {faute('client_id')}
        </div>
        <div className="saisie c2">
          <label htmlFor="ot-reference">Référence client</label>
          <input
            id="ot-reference"
            className="boite"
            type="text"
            value={saisie.reference_client}
            onChange={(e) => quandChamp('reference_client', e.target.value)}
          />
          {faute('reference_client')}
        </div>
        <div className="saisie c2">
          <label htmlFor="ot-gabarit">Gabarit demandé</label>
          <select
            id="ot-gabarit"
            className="boite"
            value={saisie.gabarit_code}
            onChange={(e) => quandChamp('gabarit_code', e.target.value)}
          >
            <option value="">Sans préférence</option>
            {gabarits.map((g) => (
              <option value={g.code} key={g.code}>
                {g.libelle}
              </option>
            ))}
          </select>
        </div>

        <div className="saisie c4">
          <label htmlFor="ot-marchandise">Nature de la marchandise</label>
          <input
            id="ot-marchandise"
            className="boite"
            type="text"
            value={saisie.marchandise}
            onChange={(e) => quandChamp('marchandise', e.target.value)}
          />
          {faute('marchandise')}
        </div>
        <div className="saisie">
          <label htmlFor="ot-prix">Prix de vente HT</label>
          <input
            id="ot-prix"
            className="boite"
            type="text"
            inputMode="decimal"
            value={saisie.prix_vente}
            onChange={(e) => quandChamp('prix_vente', e.target.value)}
          />
          {faute('prix_vente')}
        </div>
        <div className="saisie">
          <label htmlFor="ot-distance">Distance (km)</label>
          <input
            id="ot-distance"
            className="boite"
            type="text"
            inputMode="numeric"
            value={saisie.distance_km}
            onChange={(e) => quandChamp('distance_km', e.target.value)}
          />
          {faute('distance_km')}
        </div>

        <div className="saisie c6">
          <label htmlFor="ot-note">Note</label>
          <textarea
            id="ot-note"
            className="boite haute"
            value={saisie.note}
            onChange={(e) => quandChamp('note', e.target.value)}
          />
          {faute('note')}
        </div>
      </div>

      {edition.refus !== null && <p className="bandeau">{edition.refus}</p>}

      {quandEnregistrer !== undefined && (
        <div className="pied-creation-site">
          <button
            className="bouton"
            type="button"
            disabled={edition.envoi}
            onClick={quandEnregistrer}
          >
            {edition.envoi ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={edition.envoi}
            onClick={quandAnnuler}
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function VueCreation({
  p,
  creation,
}: {
  readonly p: ProprietesVueOrdres;
  readonly creation: {
    readonly ordre: EditionOrdre;
    readonly etapes: readonly EditionEtape[];
  };
}) {
  return (
    <div className="corps corps-ot">
      <div className="bandeau-fiche">
        <button className="retour" type="button" onClick={p.quandRetour}>
          ← Les OT
        </button>
        <div className="titre-fiche">
          <div className="ligne">
            <h3 className="titre-neuf">Nouvel ordre de transport</h3>
          </div>
          <span className="client">Le numéro sera attribué à l’enregistrement</span>
        </div>
      </div>

      <div className="contenu-onglet">
        <div className="section-creation">
          <p className="titre-section">La commande</p>
          <FormulaireOrdre
            edition={creation.ordre}
            clients={p.clients}
            gabarits={p.gabarits}
            quandChamp={p.quandChampOrdre}
          />
        </div>

        <div className="section-creation">
          <p className="titre-section">Les étapes</p>
          <div className="cartes-etapes">
            {creation.etapes.map((edition, rang) => (
              <div className="carte-etape" key={`etape-${String(rang)}`}>
                <div className="entete-etape">
                  <span className="rang">{rang + 1}</span>
                  <span className="type-etape">
                    {edition.saisie.type === 'chargement' ? 'Chargement' : 'Livraison'}
                  </span>
                  <span className="site-etape">
                    {edition.saisie.site_nom === ''
                      ? 'Site à choisir'
                      : edition.saisie.site_nom}
                  </span>
                  <div className="actions-etape">
                    {rang > 0 && (
                      <button
                        className="bouton bouton-discret bouton-petit"
                        type="button"
                        onClick={() => p.quandDeplacerEtapeCreation(rang, -1)}
                        aria-label={`Monter l’étape ${String(rang + 1)}`}
                        title="Monter cette étape"
                      >
                        ↑
                      </button>
                    )}
                    {rang < creation.etapes.length - 1 && (
                      <button
                        className="bouton bouton-discret bouton-petit"
                        type="button"
                        onClick={() => p.quandDeplacerEtapeCreation(rang, 1)}
                        aria-label={`Descendre l’étape ${String(rang + 1)}`}
                        title="Descendre cette étape"
                      >
                        ↓
                      </button>
                    )}
                    {creation.etapes.length > 1 && (
                      <button
                        className="bouton bouton-risque bouton-petit"
                        type="button"
                        onClick={() => p.quandRetirerEtapeCreation(rang)}
                      >
                        Retirer
                      </button>
                    )}
                  </div>
                </div>
                <div className="corps-etape">
                  <FormulaireEtape
                    edition={edition}
                    sites={p.sites}
                    motifs={p.motifs}
                    supports={p.supports}
                    vehicules={p.vehicules}
                    chauffeurs={p.chauffeurs}
                    remorques={p.remorques}
                    neuve
                    quandChamp={(cle, valeur) =>
                      p.quandChampEtapeCreation(rang, cle, valeur)
                    }
                    quandSite={(id) => p.quandSiteCreation(rang, id)}
                    quandOuvrirSite={() => p.quandOuvrirSiteCreation(rang)}
                    quandChampSite={(cle, valeur) =>
                      p.quandChampSiteCreation(rang, cle, valeur)
                    }
                    quandCreerSite={() => p.quandCreerSiteCreation(rang)}
                    quandFermerSite={() => p.quandFermerSiteCreation(rang)}
                  />
                </div>
              </div>
            ))}

            <div className="ajouter-etape">
              <button
                className="bouton bouton-discret"
                type="button"
                onClick={() => p.quandAjouterEtapeCreation('chargement')}
              >
                + Un chargement
              </button>
              <button
                className="bouton bouton-discret"
                type="button"
                onClick={() => p.quandAjouterEtapeCreation('livraison')}
              >
                + Une livraison
              </button>
            </div>
          </div>
        </div>
      </div>

      {creation.ordre.refus !== null && (
        <p className="bandeau">{creation.ordre.refus}</p>
      )}

      <div className="pied-creation">
        <span className="note-pied">Les champs marqués * sont obligatoires.</span>
        <button
          className="bouton bouton-discret"
          type="button"
          disabled={creation.ordre.envoi}
          onClick={p.quandRetour}
        >
          Annuler
        </button>
        <button
          className="bouton"
          type="button"
          disabled={creation.ordre.envoi}
          onClick={p.quandEnregistrerCreation}
        >
          {creation.ordre.envoi ? 'Enregistrement…' : 'Enregistrer l’OT'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'état
// ---------------------------------------------------------------------------

export interface ProprietesOrdres {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  /**
   * Un ordre à ouvrir tout de suite, demandé depuis un AUTRE écran — le
   * planning, où l'on clique un bloc pour voir sa fiche. `null` le reste du
   * temps.
   */
  readonly ouvrirOrdre?: string | null;
  /** Prévient que la demande a été honorée, pour qu'elle ne se rejoue pas. */
  readonly quandOrdreOuvert?: () => void;
}

function editionVide(saisie: SaisieOrdre): EditionOrdre {
  return { initiale: saisie, saisie, fautes: {}, refus: null, envoi: false };
}

function editionEtapeVide(saisie: SaisieEtape, id: string | null): EditionEtape {
  return {
    id,
    saisie,
    initiale: saisie,
    fautes: {},
    refus: null,
    envoi: false,
    site: null,
    fautesSite: {},
    confirme: false,
  };
}

export function EcranOrdres({
  jeton,
  quandAccesPerdu,
  ouvrirOrdre = null,
  quandOrdreOuvert,
}: ProprietesOrdres) {
  const [vue, setVue] = useState<'liste' | 'fiche' | 'creation'>('liste');
  const [liste, setListe] = useState<EtatListe>({ nom: 'chargement' });
  const [fiche, setFiche] = useState<EtatFiche>({ nom: 'aucune' });
  const [onglet, setOnglet] = useState<Onglet>('commande');
  /**
   * AUCUN FILTRE À L'ARRIVÉE — demandé le 07/09/2026, mot pour mot :
   * « lorsque l'on arrive sur un menu aucun filtre ne doit être appliqué de
   * base ».
   *
   * La fenêtre s'ouvrait sur le mois courant. C'était la cause du défaut du
   * 04/09 — « l'écran affichait "Aucun ordre entre le 01/09 et le 30/09" alors
   * que le seul ordre de la base était daté du 31 août » — qu'on avait alors
   * traité par un compteur (« 3 ordres hors de cette période ») plutôt qu'à sa
   * racine. Un écran qui cache par défaut oblige à comprendre pourquoi avant
   * de pouvoir travailler.
   *
   * Le compteur `hors_periode` RESTE utile : il parle dès qu'on pose une
   * fenêtre soi-même.
   */
  const [fenetre, setFenetre] = useState({ du: '', au: '' });
  const [statut, setStatut] = useState('');
  /**
   * Combien d'ordres la fenêtre de dates écarte.
   *
   * L'écran le DIT désormais, au lieu de laisser croire qu'il n'y a rien :
   * le 04/09/2026, un ordre daté du 31 août n'apparaissait pas dans une liste
   * ouverte sur septembre, et personne ne pouvait le deviner.
   */
  const [horsPeriode, setHorsPeriode] = useState(0);
  const [recherche, setRecherche] = useState('');

  const [clients, setClients] = useState<readonly LigneListe[]>([]);
  const [sites, setSites] = useState<readonly LigneListe[]>([]);
  const [gabarits, setGabarits] = useState<readonly Vehicule[]>([]);
  const [motifs, setMotifs] = useState<readonly MotifLitige[]>([]);
  /**
   * Les supports réglés par la société — les ACTIFS seulement : la liste sert à
   * proposer, et un support désactivé n'a plus à être proposé. Une étape
   * ancienne qui en porte un le garde quand même à l'écran, c'est le rôle de
   * `supportsAvec`.
   */
  const [supports, setSupports] = useState<readonly LigneReglee[]>([]);
  /** Les pièces jointes de l'ordre ouvert, et les types pour en joindre une. */
  const [documents, setDocuments] = useState<readonly DocumentJoint[]>([]);
  const [typesDocument, setTypesDocument] = useState<readonly LigneReglee[]>([]);
  const [ajoutDocument, setAjoutDocument] = useState<AjoutDocument | null>(null);
  const [envoiDocument, setEnvoiDocument] = useState(false);
  const [refusDocument, setRefusDocument] = useState<string | null>(null);
  const [vehicules, setVehicules] = useState<readonly LigneParc[]>([]);
  const [chauffeurs, setChauffeurs] = useState<readonly LigneParc[]>([]);
  const [remorques, setRemorques] = useState<readonly LigneParc[]>([]);

  const [editionOrdre, setEditionOrdre] = useState<EditionOrdre | null>(null);
  const [editionEtape, setEditionEtape] = useState<EditionEtape | null>(null);
  const [creation, setCreation] = useState<{
    readonly ordre: EditionOrdre;
    readonly etapes: readonly EditionEtape[];
  } | null>(null);

  const [validation, setValidation] = useState<SaisieValidation | null>(null);
  const [tempsPasse, setTempsPasse] = useState('');
  /**
   * LE COÛT DE L'ORDRE OUVERT, relu à chaque ouverture de fiche.
   *
   * Un appel à part, et non un champ de la fiche : le calcul interroge les
   * coûts de ressource, les frais et l'indice gazole, et le faire à chaque
   * ligne de la LISTE des ordres serait une requête par ordre.
   */
  const [cout, setCout] = useState<CoutOrdre | null>(null);
  const [litige, setLitige] = useState<SaisieLitige | null>(null);
  /**
   * LA RÉSOLUTION D'UN LITIGE, depuis le bandeau de la carte d'étape.
   *
   * Ajoutée le 07/09/2026 : le litige a quitté le formulaire de l'étape, et
   * sans ce geste il aurait fallu passer par l'écran Litiges pour clore ce
   * qu'on venait d'ouvrir.
   */
  const [resolution, setResolution] = useState<{
    readonly litigeId: string;
    readonly texte: string;
    readonly jour: string;
    readonly heure: string;
  } | null>(null);
  /**
   * L'ÉCHANGE DE PALETTES EN COURS DE SAISIE.
   *
   * Un état à part et non un champ du formulaire d'étape : il s'écrit par une
   * autre route (`PUT /emballage/etapes/:id`) et il n'existe qu'une fois
   * l'étape enregistrée. Même forme que la boîte de litige, juste au-dessus.
   */
  const [echange, setEchange] = useState<{
    readonly etapeId: string;
    readonly saisie: SaisieEchange;
  } | null>(null);
  /**
   * LE PANNEAU D'AFFRÈTEMENT, et les transporteurs que le moteur propose.
   *
   * Les transporteurs sont cherchés À L'OUVERTURE, avec l'axe déduit de
   * l'ordre : c'est le panneau du cadrage §6, où l'on ne saisit ni l'axe ni le
   * matériel. Les charger d'avance, pour tous les ordres, ferait une recherche
   * par ligne de liste.
   */
  const [affretement, setAffretement] = useState<SaisieAffretement | null>(null);
  const [proposes, setProposes] = useState<readonly TransporteurTrouve[]>([]);
  const [chercheEnCours, setChercheEnCours] = useState(false);
  /**
   * L'ANNUAIRE ENTIER, chargé en même temps que la recherche par axe.
   *
   * Deux appels au lieu d'un, à l'ouverture du panneau seulement : la liste
   * complète est ce qui rend le panneau utilisable quand aucune couverture
   * n'est saisie (voir `grouperTransporteurs`). Elle n'est PAS chargée avec la
   * liste des ordres — ce serait un appel pour un écran qui n'affrète pas.
   */
  const [tousTransporteurs, setTousTransporteurs] = useState<readonly LigneListe[]>(
    [],
  );
  /**
   * LA CRÉATION D'UN TRANSPORTEUR SANS QUITTER L'ORDRE — demandé le 07/09/2026.
   *
   * Le panneau d'affrètement reste ouvert DERRIÈRE : la saisie en cours (le
   * prix, le contact) n'est pas perdue. Le formulaire est celui de l'Annuaire,
   * au champ près — `ANNUAIRE_TRANSPORTEURS` est la seule source de vérité sur
   * ce qu'une fiche de transporteur porte.
   */
  const [nouveauTransporteur, setNouveauTransporteur] = useState<{
    readonly saisie: Saisie;
    readonly fautes: Readonly<Record<string, string>>;
    readonly refus: string | null;
  } | null>(null);

  const [action, setAction] = useState({
    confirmeAnnulation: false,
    confirmeSuppression: false,
    envoi: false,
    refus: null as string | null,
  });

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  // Les référentiels, une seule fois : clients, sites, gabarits, motifs de
  // litige. Ils ne changent pas pendant qu'on saisit un ordre.
  useEffect(() => {
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const [c, s, g, m, v, ch, r, sup, td] = await Promise.all([
        lireListe(j, ANNUAIRE_CLIENTS),
        lireListe(j, ANNUAIRE_SITES),
        lireVehicules(j),
        lireMotifsLitige(j),
        lireParc(j, PARC_VEHICULES),
        lireParc(j, PARC_CHAUFFEURS),
        lireParc(j, PARC_REMORQUES),
        lireListeReglee(j, 'supports'),
        lireListeReglee(j, 'types_document'),
      ]);
      if (abandonne) return;
      if (c.etat === 'ok') setClients(c.valeur);
      if (s.etat === 'ok') setSites(s.valeur);
      if (g.etat === 'ok') setGabarits(g.valeur);
      if (m.etat === 'ok') setMotifs(m.valeur);
      if (v.etat === 'ok') setVehicules(v.valeur);
      if (ch.etat === 'ok') setChauffeurs(ch.valeur);
      if (r.etat === 'ok') setRemorques(r.valeur);
      if (sup.etat === 'ok') {
        setSupports(sup.valeur.filter((l) => l.actif));
      }
      if (td.etat === 'ok') setTypesDocument(td.valeur);
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton]);

  // La liste, à chaque changement de fenêtre ou de statut. Le texte, lui, ne
  // rappelle jamais l'API : il filtre la liste déjà chargée.
  useEffect(() => {
    let abandonne = false;
    setListe({ nom: 'chargement' });
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lu = await lireOrdres(j, { ...fenetre, statut });
      if (abandonne) return;
      if (lu.etat === 'ok') {
        setListe({ nom: 'prete', ordres: lu.valeur.ordres });
        setHorsPeriode(lu.valeur.horsPeriode);
      } else {
        if (lu.cause === 'acces') quandAccesPerdu();
        setListe({ nom: 'panne' });
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [fenetre, statut, avecJeton, quandAccesPerdu]);

  /** Range la fiche rendue par l'API, dans la liste ET dans la fiche ouverte. */
  const poser = useCallback((rendue: FicheOrdre) => {
    const ordre = rendue.ordre_transport;
    setFiche({ nom: 'prete', id: ordre.id, fiche: rendue });
    setListe((actuelle) => {
      if (actuelle.nom !== 'prete') return actuelle;
      const connu = actuelle.ordres.some((o) => o.id === ordre.id);
      return {
        nom: 'prete',
        ordres: connu
          ? actuelle.ordres.map((o) => (o.id === ordre.id ? ordre : o))
          : [...actuelle.ordres, ordre],
      };
    });
  }, []);

  const ouvrir = useCallback(
    (id: string) => {
      setVue('fiche');
      setOnglet('commande');
      setEditionOrdre(null);
      setEditionEtape(null);
      setAction({
        confirmeAnnulation: false,
        confirmeSuppression: false,
        envoi: false,
        refus: null,
      });
      setFiche({ nom: 'chargement', id });
      setCout(null);
      setTempsPasse('');

      void (async () => {
        const j = await avecJeton();
        if (j === null) return;
        const lu = await lireOrdre(j, id);
        if (lu.etat === 'ok') {
          setFiche({ nom: 'prete', id, fiche: lu.valeur });
          // Les pièces jointes suivent la fiche : deux appels à l'ouverture,
          // et aucun ensuite tant qu'on ne joint rien.
          const pieces = await lireDocuments(j, id);
          if (pieces.etat === 'ok') setDocuments(pieces.valeur);
          // LE COÛT AUSSI. Un troisième appel, à l'ouverture seulement : le
          // calcul interroge les coûts de ressource, les frais et l'indice
          // gazole, et le faire pour chaque ligne de la liste serait une
          // requête par ordre.
          const lecture = await lireLeCout(j, id);
          if (lecture.etat === 'ok') setCout(lecture.valeur);
        } else {
          if (lu.cause === 'acces') quandAccesPerdu();
          setFiche({ nom: 'panne', id, cause: lu.cause });
        }
      })();
      // `useCallback` depuis le 04/09/2026 : le planning peut demander l'ouverture
      // d'un ordre, et l'effet qui l'écoute a besoin d'une fonction stable.
    },
    [avecJeton, quandAccesPerdu],
  );

  // Une demande venue du planning : on ouvre la fiche, puis on l'oublie —
  // sans quoi revenir à la liste rouvrirait la même fiche sans fin.
  useEffect(() => {
    if (ouvrirOrdre === null) return;
    ouvrir(ouvrirOrdre);
    if (quandOrdreOuvert !== undefined) quandOrdreOuvert();
  }, [ouvrirOrdre, ouvrir, quandOrdreOuvert]);

  /**
   * Recopie l'adresse du site choisi dans l'étape.
   *
   * Le nom, le code postal et la ville sont DÉJÀ dans la liste ; seule l'adresse
   * demande de lire la fiche. Si cette lecture échoue, on n'invente rien : à la
   * création l'API recopiera l'adresse elle-même, et à la modification le champ
   * reste tel qu'il était.
   */
  const adresseDuSite = useCallback(
    async (siteId: string, poserAdresse: (adresse: string) => void): Promise<void> => {
      const j = await avecJeton();
      if (j === null) return;
      const lu = await lireFiche(j, ANNUAIRE_SITES, siteId);
      if (lu.etat !== 'ok') return;
      poserAdresse(valeur(lu.valeur.fiche, 'adresse') ?? '');
    },
    [avecJeton],
  );

  /** Le site choisi, tel qu'il se recopie dans une étape. */
  const depuisSite = (siteId: string): Partial<SaisieEtape> => {
    const site = sites.find((s) => s.id === siteId);
    if (site === undefined) return { site_id: '' };
    return {
      site_id: siteId,
      site_nom: site.nom,
      site_code_postal: site.code_postal ?? '',
      site_ville: site.ville ?? '',
    };
  };

  // -------------------------------------------------------------------------
  // Les écritures
  // -------------------------------------------------------------------------

  const enregistrerOrdre = () => {
    if (editionOrdre === null || fiche.nom !== 'prete') return;
    const fautes = validerOrdre(editionOrdre.saisie);
    if (Object.keys(fautes).length > 0) {
      setEditionOrdre({ ...editionOrdre, fautes, refus: null });
      return;
    }
    const change = differenceOrdre(editionOrdre.initiale, editionOrdre.saisie);
    if (Object.keys(change).length === 0) {
      setEditionOrdre(null);
      return;
    }

    const encours = editionOrdre;
    const id = fiche.id;
    setEditionOrdre({ ...encours, fautes: {}, refus: null, envoi: true });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await modifierOrdre(j, id, change);
      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setEditionOrdre(null);
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setEditionOrdre({ ...encours, envoi: false, refus: REFUS[resultat.cause] });
    })();
  };

  const enregistrerEtape = () => {
    if (editionEtape === null || fiche.nom !== 'prete') return;
    const fautes = validerEtape(editionEtape.saisie);
    if (Object.keys(fautes).length > 0) {
      setEditionEtape({ ...editionEtape, fautes, refus: null });
      return;
    }

    const encours = editionEtape;
    const otId = fiche.id;
    setEditionEtape({ ...encours, fautes: {}, refus: null, envoi: true });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat =
        encours.id === null
          ? await ajouterEtape(j, otId, corpsEtape(encours.saisie, true))
          : await modifierEtape(
              j,
              otId,
              encours.id,
              corpsEtape(encours.saisie, false, encours.initiale),
            );

      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setEditionEtape(null);
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setEditionEtape({ ...encours, envoi: false, refus: REFUS[resultat.cause] });
    })();
  };

  /** Le refus d'une écriture d'étape, posé au même endroit pour les deux boîtes. */
  const finirLEcriture = (resultat: { etat: 'ok' | 'echec'; cause?: EchecApi }) => {
    setAction({
      confirmeAnnulation: false,
      confirmeSuppression: false,
      envoi: false,
      refus:
        resultat.etat === 'ok' || resultat.cause === undefined
          ? null
          : REFUS[resultat.cause],
    });
  };

  /**
   * VALIDER passe par une boîte, DÉVALIDER non.
   *
   * Valider demande QUAND — l'utilisateur l'a demandé le 04/09/2026, et poser
   * l'instant de la saisie en silence donnait une heure fausse dès qu'on valide
   * le lendemain matin les livraisons de la veille. Dévalider n'a rien à
   * demander : il n'y a plus d'heure de réalisation à connaître.
   */
  const basculerFait = (id: string) => {
    if (fiche.nom !== 'prete') return;
    const etape = fiche.fiche.etapes.find((e) => e.id === id);
    if (etape === undefined) return;

    if (!etape.fait) {
      setLitige(null);
      setEchange(null);
      setAction((a) => ({ ...a, refus: null }));
      setValidation({ etapeId: id, jour: aujourdhui(), heure: maintenant() });
      return;
    }

    const otId = fiche.id;
    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await modifierEtape(j, otId, id, { fait: false });
      if (resultat.etat === 'ok') poser(resultat.valeur);
      else if (resultat.cause === 'acces') quandAccesPerdu();
      finirLEcriture(resultat);
    })();
  };

  /** La boîte de validation, confirmée. */
  const confirmerValidation = () => {
    if (validation === null || fiche.nom !== 'prete') return;
    if (refusValidation(validation) !== null) return;
    const otId = fiche.id;
    const faitLe = instantDepuis(validation.jour, validation.heure);
    if (faitLe === null) return;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await modifierEtape(j, otId, validation.etapeId, {
        fait: true,
        fait_le: faitLe,
      });
      if (resultat.etat !== 'ok') {
        if (resultat.cause === 'acces') quandAccesPerdu();
        finirLEcriture(resultat);
        return;
      }

      /**
       * LE TEMPS PASSÉ, DANS LA FOULÉE — et le coût se fige derrière.
       *
       * Écrit APRÈS la validation, et non avec : ce sont deux tables et deux
       * routes. Si la seconde rate, l'étape reste validée — ce qui est le bon
       * sens : le camion est bien arrivé, seul le temps manque, et il se
       * rattrape par « Recalculer ».
       */
      const minutes = minutesSaisies(tempsPasse);
      if (minutes !== null) {
        await poserLExecution(j, otId, { temps_reel_minutes: minutes });
        setTempsPasse('');
        const lecture = await lireLeCout(j, otId);
        if (lecture.etat === 'ok') setCout(lecture.valeur);
      }

      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') {
        poser(relue.valeur);
        setValidation(null);
      } else if (relue.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(relue);
    })();
  };

  /**
   * La boîte de litige, confirmée.
   *
   * Le motif est CRÉÉ D'ABORD s'il est neuf, dans un appel séparé : il n'existe
   * pas de route qui fasse les deux, et en inventer une pour ce seul geste
   * mêlerait un paramétrage à une écriture de transport. Si la création réussit
   * et que l'étape échoue, le motif reste — ce qui est sans dommage : il
   * resservira.
   */
  /**
   * RÉSOUDRE : dire COMMENT, obligatoirement.
   *
   * L'API le refuse sinon, et la base aussi (`litiges_resolution_complete`) —
   * mais on ne fait pas partir un appel qu'on sait perdu.
   */
  const enregistrerLaResolution = () => {
    if (resolution === null || fiche.nom !== 'prete') return;
    if (resolution.texte.trim() === '') {
      setAction((a) => ({ ...a, refus: 'Dites comment le litige a été réglé.' }));
      return;
    }
    const otId = fiche.id;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const resoluLe = instantDepuis(resolution.jour, resolution.heure);
      const fait = await resoudreUnLitige(j, resolution.litigeId, {
        resolution: resolution.texte.trim(),
        ...(resoluLe === null ? {} : { resolu_le: resoluLe }),
      });
      if (fait.etat !== 'ok') {
        if (fait.cause === 'acces') quandAccesPerdu();
        finirLEcriture(fait);
        return;
      }

      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') {
        poser(relue.valeur);
        setResolution(null);
      } else if (relue.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(relue);
    })();
  };

  const ouvrirLeLitige = () => {
    if (litige === null || fiche.nom !== 'prete') return;
    if (refusLitige(litige) !== null) return;
    const otId = fiche.id;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      let motifId = litige.motifId;
      if (litige.motifNouveau !== null) {
        const cree = await creerMotifLitige(j, litige.motifNouveau.trim());
        if (cree.etat !== 'ok') {
          if (cree.cause === 'acces') quandAccesPerdu();
          finirLEcriture(cree);
          return;
        }
        setMotifs((actuels) => [...actuels, cree.valeur]);
        motifId = cree.valeur.id;
      }

      // LE LITIGE A SA PROPRE ROUTE depuis le 07/09/2026 : il ne se pose plus
      // par un PATCH de l'étape. Et il ne rend plus la fiche : on la relit,
      // pour que le bandeau et le compteur viennent de la base.
      const ouvertLe = instantDepuis(litige.jour, litige.heure);
      const pose = await ouvrirUnLitige(j, litige.etapeId, {
        motif_litige_id: motifId,
        ...(litige.note.trim() === '' ? {} : { note: litige.note.trim() }),
        ...(ouvertLe === null ? {} : { ouvert_le: ouvertLe }),
      });
      if (pose.etat !== 'ok') {
        if (pose.cause === 'acces') quandAccesPerdu();
        finirLEcriture(pose);
        return;
      }

      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') {
        poser(relue.valeur);
        setLitige(null);
      } else if (relue.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(relue);
    })();
  };


  /**
   * ENREGISTRER L'ÉCHANGE — ou l'effacer, ce qui est le même geste avec un
   * corps vide.
   *
   * L'écriture passe par le module Emballage, puis la fiche est RELUE en
   * entier. Elle ne se met pas à jour toute seule : l'échange y arrive par une
   * jointure de `SELECTION_ETAPES`, et l'API vient peut-être aussi de créer un
   * bon. Recopier le résultat à la main dans l'état laisserait la fiche dire
   * autre chose que la base.
   */
  const enregistrerLEchange = (effacer: boolean) => {
    if (echange === null || fiche.nom !== 'prete') return;
    if (!effacer && refusEchange(echange.saisie) !== null) return;
    const otId = fiche.id;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const pose = await poserLEchange(
        j,
        echange.etapeId,
        effacer ? {} : corpsEchange(echange.saisie),
      );
      if (pose.etat !== 'ok') {
        if (pose.cause === 'acces') quandAccesPerdu();
        finirLEcriture(pose);
        return;
      }

      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') {
        poser(relue.valeur);
        setEchange(null);
      } else if (relue.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(relue);
    })();
  };


  /**
   * OUVRIR LE PANNEAU, et chercher qui fait cet axe.
   *
   * L'axe se déduit des colonnes que la liste porte déjà — `chargement_ville`
   * et `livraison_ville` viennent du premier chargement et de la dernière
   * livraison. On ne redemande rien à personne.
   *
   * La recherche N'ÉCHOUE PAS le panneau : si elle rate, la liste est vide et
   * le panneau le dit. Refuser d'ouvrir pour un défaut de réseau empêcherait
   * d'affréter un ordre dont on connaît déjà le transporteur.
   */
  const ouvrirLAffretement = () => {
    if (fiche.nom !== 'prete') return;
    const ordre = fiche.fiche.ordre_transport;

    setValidation(null);
    setLitige(null);
    setEchange(null);
    setAction((a) => ({ ...a, refus: null }));
    setAffretement(AFFRETEMENT_VIDE);
    setProposes([]);
    setTousTransporteurs([]);
    setNouveauTransporteur(null);
    setChercheEnCours(true);

    void (async () => {
      const j = await avecJeton();
      if (j === null) {
        setChercheEnCours(false);
        return;
      }
      // L'AXE VIENT DE L'ORDRE, calculé par l'API : le pays est traduit en
      // base, par la table `pays`. Le recalculer ici ferait une seconde
      // traduction, et deux traductions finissent par diverger.
      const [trouve, annuaire] = await Promise.all([
        chercherTransporteurs(j, {
          depart: ordre.zone_depart,
          arrivee: ordre.zone_arrivee,
          vehicule: ordre.gabarit_code,
        }),
        lireListe(j, ANNUAIRE_TRANSPORTEURS),
      ]);
      if (trouve.etat === 'ok') setProposes(trouve.valeur.transporteurs);
      if (annuaire.etat === 'ok') setTousTransporteurs(annuaire.valeur);
      setChercheEnCours(false);
    })();
  };

  /**
   * OUVRIR LE FORMULAIRE DE CRÉATION, par-dessus le panneau d'affrètement.
   *
   * Le choix du déroulant est remis à vide : « + Nouveau transporteur… » n'est
   * pas une sélection, c'est un geste. Le laisser posé enverrait `+nouveau`
   * comme identifiant si la création était abandonnée.
   */
  const ouvrirLeNouveauTransporteur = () => {
    setAffretement((a) => (a === null ? a : { ...a, transporteurId: '' }));
    setNouveauTransporteur({
      saisie: saisieVide(ANNUAIRE_TRANSPORTEURS),
      fautes: {},
      refus: null,
    });
  };

  /**
   * CRÉER, puis RELIRE L'ANNUAIRE et sélectionner le nouveau venu.
   *
   * On relit plutôt que d'ajouter la fiche rendue à la liste en mémoire : c'est
   * la même règle que partout ici — ce que l'écran affiche vient de la base, et
   * non d'une recopie qui finirait par diverger.
   */
  const enregistrerLeNouveauTransporteur = () => {
    const encours = nouveauTransporteur;
    if (encours === null) return;

    const fautes = validerFiche(encours.saisie, ANNUAIRE_TRANSPORTEURS);
    if (Object.keys(fautes).length > 0) {
      setNouveauTransporteur({ ...encours, fautes, refus: null });
      return;
    }

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const cree = await creerFiche(
        j,
        ANNUAIRE_TRANSPORTEURS,
        pourCreation(encours.saisie, ANNUAIRE_TRANSPORTEURS),
      );
      if (cree.etat !== 'ok') {
        if (cree.cause === 'acces') quandAccesPerdu();
        finirLEcriture(cree);
        return;
      }

      const neuf = cree.valeur.fiche.id;
      const annuaire = await lireListe(j, ANNUAIRE_TRANSPORTEURS);
      if (annuaire.etat === 'ok') setTousTransporteurs(annuaire.valeur);
      setAffretement((a) => (a === null ? a : { ...a, transporteurId: neuf }));
      setNouveauTransporteur(null);
      finirLEcriture({ etat: 'ok' });
    })();
  };

  /**
   * AFFRÉTER, puis relire la fiche.
   *
   * L'API fige le nom, le contact, le prix et l'axe, et l'ordre sort du
   * planning dans la foulée. Recopier le résultat à la main dans l'état
   * laisserait la fiche dire autre chose que la base.
   */
  const enregistrerLAffretement = () => {
    if (affretement === null || fiche.nom !== 'prete') return;
    const otId = fiche.id;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const fait = await affreter(j, otId, corpsAffretement(affretement));
      if (fait.etat !== 'ok') {
        if (fait.cause === 'acces') quandAccesPerdu();
        finirLEcriture(fait);
        return;
      }

      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') {
        poser(relue.valeur);
        setAffretement(null);
      } else if (relue.cause === 'acces') {
        quandAccesPerdu();
      }
      finirLEcriture(relue);
    })();
  };

  /**
   * ANNULER L'AFFRÈTEMENT — la ligne reste, l'ordre revient au planning.
   */
  const annulerLeAffretement = () => {
    if (fiche.nom !== 'prete') return;
    const ordre = fiche.fiche.ordre_transport;
    if (ordre.affretement_id === null) return;
    const otId = fiche.id;

    setAction((a) => ({ ...a, envoi: true, refus: null }));
    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const fait = await annulerLAffretement(j, ordre.affretement_id as string);
      if (fait.etat !== 'ok') {
        if (fait.cause === 'acces') quandAccesPerdu();
        finirLEcriture(fait);
        return;
      }
      const relue = await lireOrdre(j, otId);
      if (relue.etat === 'ok') poser(relue.valeur);
      finirLEcriture(relue);
    })();
  };

  /**
   * Monter ou descendre une étape.
   *
   * L'écran envoie L'ORDRE ENTIER, et non le rang de l'étape déplacée : deux
   * `PATCH` de rang échoueraient au premier, l'index unique (ot_id, rang) étant
   * vérifié à chaque ligne. La route `PUT` renumérote tout dans une seule
   * transaction.
   */
  const deplacerEtape = (rang: number, pas: -1 | 1) => {
    if (fiche.nom !== 'prete') return;
    const etapes = [...fiche.fiche.etapes];
    const vers = rang + pas;
    if (vers < 0 || vers >= etapes.length) return;
    const [deplacee] = etapes.splice(rang, 1);
    if (deplacee === undefined) return;
    etapes.splice(vers, 0, deplacee);

    const otId = fiche.id;
    setAction((a) => ({ ...a, envoi: true, refus: null }));

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await reordonnerEtapes(
        j,
        otId,
        etapes.map((e) => e.id),
      );
      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setAction({
          confirmeAnnulation: false,
          confirmeSuppression: false,
          envoi: false,
          refus: null,
        });
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setAction({
        confirmeAnnulation: false,
        confirmeSuppression: false,
        envoi: false,
        refus: REFUS[resultat.cause],
      });
    })();
  };

  const supprimerLEtape = () => {
    if (editionEtape === null || editionEtape.id === null || fiche.nom !== 'prete') {
      return;
    }
    const encours = editionEtape;
    const otId = fiche.id;
    const etapeId = encours.id as string;
    setEditionEtape({ ...encours, envoi: true, refus: null });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await supprimerEtape(j, otId, etapeId);
      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setEditionEtape(null);
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setEditionEtape({ ...encours, envoi: false, refus: REFUS[resultat.cause] });
    })();
  };

  /**
   * Crée le site replié dans la carte, puis le rattache à l'étape.
   *
   * Il est ajouté à la liste des sites RENDUE PAR L'API, jamais à ce qui a été
   * saisi : la base a pu normaliser une valeur.
   */
  const creerLeSite = (
    edition: EditionEtape,
    poserEdition: (e: EditionEtape) => void,
  ) => {
    const saisieSite = edition.site;
    if (saisieSite === null) return;
    const fautesSite = validerSite(saisieSite);
    if (Object.keys(fautesSite).length > 0) {
      poserEdition({ ...edition, fautesSite });
      return;
    }

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await creerFiche(
        j,
        ANNUAIRE_SITES,
        pourCreationSite(saisieSite),
      );
      if (resultat.etat !== 'ok') {
        if (resultat.cause === 'acces') quandAccesPerdu();
        poserEdition({ ...edition, refus: REFUS[resultat.cause] });
        return;
      }

      const cree = resultat.valeur.fiche;
      setSites((actuels) => [...actuels, cree]);
      poserEdition({
        ...edition,
        site: null,
        fautesSite: {},
        refus: null,
        saisie: {
          ...edition.saisie,
          site_id: cree.id,
          site_nom: cree.nom,
          site_adresse: valeur(cree, 'adresse') ?? '',
          site_code_postal: cree.code_postal ?? '',
          site_ville: cree.ville ?? '',
        },
      });
    })();
  };

  /**
   * Annuler et rouvrir, les deux seules décisions humaines sur le statut.
   *
   * Elles passent par leurs propres routes et non par une modification de
   * champ : le statut se déduit des étapes, et un `PATCH { statut }` poserait
   * une valeur que le premier changement d'étape écraserait. L'API le refuse
   * d'ailleurs depuis le 04/09/2026.
   *
   * Le statut affiché après coup est CELUI QUE L'API REND, jamais celui qu'on
   * espérait : à la réouverture, c'est l'état réel des étapes.
   */
  const changerLAnnulation = (
    appel: (jeton: string, id: string) => Promise<Resultat<FicheOrdre>>,
  ) => {
    if (fiche.nom !== 'prete') return;
    const id = fiche.id;
    setAction((a) => ({ ...a, envoi: true, refus: null }));

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await appel(j, id);
      if (resultat.etat === 'ok') {
        poser(resultat.valeur);
        setAction({
          confirmeAnnulation: false,
          confirmeSuppression: false,
          envoi: false,
          refus: null,
        });
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setAction({
        confirmeAnnulation: false,
        confirmeSuppression: false,
        envoi: false,
        refus: REFUS[resultat.cause],
      });
    })();
  };

  const annulerLOrdre = () => {
    changerLAnnulation(annulerOrdre);
  };

  const rouvrirLOrdre = () => {
    changerLAnnulation(rouvrirOrdre);
  };

  const supprimerLOrdre = () => {
    if (fiche.nom !== 'prete') return;
    const id = fiche.id;
    setAction((a) => ({ ...a, envoi: true, refus: null }));

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await supprimerOrdre(j, id);
      if (resultat.etat === 'ok') {
        setListe((actuelle) =>
          actuelle.nom === 'prete'
            ? { nom: 'prete', ordres: actuelle.ordres.filter((o) => o.id !== id) }
            : actuelle,
        );
        setFiche({ nom: 'aucune' });
        setVue('liste');
        setAction({
          confirmeAnnulation: false,
          confirmeSuppression: false,
          envoi: false,
          refus: null,
        });
        return;
      }
      if (resultat.cause === 'acces') quandAccesPerdu();
      setAction({
        confirmeAnnulation: false,
        confirmeSuppression: false,
        envoi: false,
        refus: REFUS[resultat.cause],
      });
    })();
  };

  /**
   * La création : l'ordre D'ABORD, ses étapes ENSUITE.
   *
   * L'ordre doit exister avant qu'on puisse lui accrocher quoi que ce soit —
   * les étapes sont sous son chemin. Si une étape échoue, l'ordre reste : il
   * est ouvert sur sa fiche avec ce qui a été créé, et le message dit laquelle
   * manque. C'est mieux que d'effacer un ordre déjà numéroté, qui trouerait la
   * numérotation.
   */
  const enregistrerCreation = () => {
    if (creation === null) return;

    const fautesOrdre = validerOrdre(creation.ordre.saisie);
    if (Object.keys(fautesOrdre).length > 0) {
      setCreation({
        ...creation,
        ordre: { ...creation.ordre, fautes: fautesOrdre, refus: null },
      });
      return;
    }

    const etapesFautives = creation.etapes.map((e) => validerEtape(e.saisie));
    if (etapesFautives.some((f) => Object.keys(f).length > 0)) {
      setCreation({
        ...creation,
        ordre: {
          ...creation.ordre,
          refus: 'Chaque étape demande un site et une date.',
        },
        etapes: creation.etapes.map((e, rang) => ({
          ...e,
          fautes: etapesFautives[rang] ?? {},
        })),
      });
      return;
    }

    const encours = creation;
    setCreation({
      ...encours,
      ordre: { ...encours.ordre, fautes: {}, refus: null, envoi: true },
    });

    void (async () => {
      const j = await avecJeton();
      if (j === null) return;

      const cree = await creerOrdre(j, pourCreationOrdre(encours.ordre.saisie));
      if (cree.etat !== 'ok') {
        if (cree.cause === 'acces') quandAccesPerdu();
        setCreation({
          ...encours,
          ordre: { ...encours.ordre, envoi: false, refus: REFUS[cree.cause] },
        });
        return;
      }

      const id = cree.valeur.ordre_transport.id;
      let derniere = cree.valeur;
      for (const etape of encours.etapes) {
        const ajout = await ajouterEtape(j, id, corpsEtape(etape.saisie, true));
        if (ajout.etat !== 'ok') {
          // L'ordre EXISTE : on l'ouvre plutôt que de le laisser invisible, et
          // on dit ce qui manque.
          poser(derniere);
          setCreation(null);
          setVue('fiche');
          setOnglet('etapes');
          setAction({
            confirmeAnnulation: false,
            confirmeSuppression: false,
            envoi: false,
            refus: `L’ordre a été créé, mais une étape a été refusée : ${REFUS[ajout.cause]}`,
          });
          return;
        }
        derniere = ajout.valeur;
      }

      poser(derniere);
      setCreation(null);
      setVue('fiche');
      setOnglet('commande');
    })();
  };

  // -------------------------------------------------------------------------

  const majEtapeCreation = (rang: number, changement: Partial<EditionEtape>) => {
    setCreation((actuelle) =>
      actuelle === null
        ? actuelle
        : {
            ...actuelle,
            etapes: actuelle.etapes.map((e, i) =>
              i === rang ? { ...e, ...changement } : e,
            ),
          },
    );
  };

  return (
    <VueOrdres
      vue={vue}
      liste={liste}
      fiche={fiche}
      onglet={onglet}
      fenetre={fenetre}
      horsPeriode={horsPeriode}
      quandToutesLesDates={() => setFenetre({ du: '', au: '' })}
      statut={statut}
      recherche={recherche}
      clients={clients}
      sites={sites}
      gabarits={gabarits}
      motifs={motifs}
      supports={supports}
      vehicules={vehicules}
      chauffeurs={chauffeurs}
      remorques={remorques}
      editionOrdre={editionOrdre}
      editionEtape={editionEtape}
      creation={creation}
      actions={{
        confirmeAnnulation: action.confirmeAnnulation,
        confirmeSuppression: action.confirmeSuppression,
        envoi: action.envoi,
        refus: action.refus,
        quandAnnulerOrdre: annulerLOrdre,
        quandRouvrirOrdre: rouvrirLOrdre,
        quandSupprimerOrdre: supprimerLOrdre,
        quandDemanderAnnulation: () =>
          setAction({
            confirmeAnnulation: true,
            confirmeSuppression: false,
            envoi: false,
            refus: null,
          }),
        quandDemanderSuppression: () =>
          setAction({
            confirmeAnnulation: false,
            confirmeSuppression: true,
            envoi: false,
            refus: null,
          }),
        quandRenoncer: () =>
          setAction({
            confirmeAnnulation: false,
            confirmeSuppression: false,
            envoi: false,
            refus: null,
          }),
      }}
      quandFenetre={setFenetre}
      quandStatut={setStatut}
      quandRecherche={setRecherche}
      quandOuvrir={ouvrir}
      quandRetour={() => {
        setVue('liste');
        setFiche({ nom: 'aucune' });
        setEditionOrdre(null);
        setEditionEtape(null);
        setCreation(null);
      }}
      quandOnglet={(o) => {
        setOnglet(o);
        setEditionOrdre(null);
        setEditionEtape(null);
        setRefusDocument(null);
      }}
      documents={documents}
      typesDocument={typesDocument}
      ajoutDocument={ajoutDocument}
      envoiDocument={envoiDocument}
      refusDocument={refusDocument}
      quandOuvrirAjout={() => {
        setRefusDocument(null);
        setAjoutDocument({ typeId: '', fichier: null });
      }}
      quandChampAjout={(typeId) =>
        setAjoutDocument((actuel) => (actuel === null ? null : { ...actuel, typeId }))
      }
      quandFichier={(fichier) => {
        setRefusDocument(
          fichier !== null && fichier.size > POIDS_MAXIMUM
            ? 'Ce fichier dépasse 20 Mo. Réduisez-le, ou joignez-en un autre.'
            : null,
        );
        setAjoutDocument((actuel) => (actuel === null ? null : { ...actuel, fichier }));
      }}
      quandFermerAjout={() => {
        setAjoutDocument(null);
        setRefusDocument(null);
      }}
      /**
       * JOINDRE : la ligne d'abord, le fichier ensuite, la confirmation enfin.
       *
       * Si l'envoi du fichier échoue, la ligne reste en « envoi inachevé » et
       * se voit à l'écran — c'est voulu. L'inverse, effacer la ligne en
       * silence, laisserait croire que rien n'a été tenté.
       */
      quandJoindre={() => {
        if (fiche.nom !== 'prete' || ajoutDocument === null) return;
        const fichier = ajoutDocument.fichier;
        if (fichier === null) return;
        if (fichier.size > POIDS_MAXIMUM) {
          setRefusDocument('Ce fichier dépasse 20 Mo.');
          return;
        }

        const otId = fiche.id;
        setEnvoiDocument(true);
        setRefusDocument(null);

        void (async () => {
          const j = await avecJeton();
          if (j === null) {
            setEnvoiDocument(false);
            return;
          }

          const declare = await declarerLeDocument(j, otId, {
            nom: fichier.name,
            type_id: ajoutDocument.typeId === '' ? null : ajoutDocument.typeId,
            poids: fichier.size,
          });
          if (declare.etat !== 'ok') {
            if (declare.cause === 'acces') quandAccesPerdu();
            setRefusDocument(REFUS[declare.cause]);
            setEnvoiDocument(false);
            return;
          }

          const envoye = await envoyerLeFichier(j, declare.valeur.chemin, fichier);
          if (envoye.etat !== 'ok') {
            setRefusDocument(
              envoye.cause === 'trop_gros'
                ? 'Le rangement a refusé ce fichier : il est trop gros.'
                : envoye.cause === 'refus'
                  ? 'Le rangement a refusé ce fichier. Rechargez l’écran et réessayez.'
                  : 'Le rangement ne répond pas. La pièce reste à reprendre.',
            );
            const pieces = await lireDocuments(j, otId);
            if (pieces.etat === 'ok') setDocuments(pieces.valeur);
            setEnvoiDocument(false);
            return;
          }

          await confirmerLeDocument(j, otId, declare.valeur.id);
          const pieces = await lireDocuments(j, otId);
          if (pieces.etat === 'ok') setDocuments(pieces.valeur);
          setAjoutDocument(null);
          setEnvoiDocument(false);
        })();
      }}
      quandOuvrirDocument={(document) => {
        setRefusDocument(null);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const adresse = await adresseDuFichier(j, document.chemin);
          if (adresse.etat !== 'ok') {
            setRefusDocument(
              'Ce fichier est introuvable dans le rangement. Il a pu être supprimé.',
            );
            return;
          }
          // Un onglet, et pas un aperçu dans l'écran : dit dans la maquette.
          window.open(adresse.valeur, '_blank', 'noopener');
        })();
      }}
      /**
       * SUPPRIMER : le fichier d'abord, la ligne ensuite.
       *
       * Si l'effacement du fichier échoue, rien n'a bougé et l'écran le dit.
       * Dans l'autre ordre, un fichier resterait sans ligne pour le désigner —
       * invisible, et payant.
       */
      quandSupprimerDocument={(document) => {
        if (fiche.nom !== 'prete') return;
        const otId = fiche.id;
        setEnvoiDocument(true);
        setRefusDocument(null);

        void (async () => {
          const j = await avecJeton();
          if (j === null) {
            setEnvoiDocument(false);
            return;
          }
          const efface = await effacerLeFichier(j, document.chemin);
          if (efface.etat !== 'ok') {
            setRefusDocument(
              'Le fichier n’a pas pu être effacé : rien n’a été supprimé.',
            );
            setEnvoiDocument(false);
            return;
          }
          const retire = await supprimerLeDocument(j, otId, document.id);
          if (retire.etat !== 'ok') {
            if (retire.cause === 'acces') quandAccesPerdu();
            setRefusDocument(REFUS[retire.cause]);
          }
          const pieces = await lireDocuments(j, otId);
          if (pieces.etat === 'ok') setDocuments(pieces.valeur);
          setEnvoiDocument(false);
        })();
      }}
      quandModifierOrdre={() => {
        if (fiche.nom !== 'prete') return;
        setOnglet('commande');
        setEditionOrdre(editionVide(ordreDepuis(fiche.fiche.ordre_transport)));
      }}
      quandChampOrdre={(cle, v) => {
        if (creation !== null) {
          setCreation({
            ...creation,
            ordre: {
              ...creation.ordre,
              saisie: { ...creation.ordre.saisie, [cle]: v },
            },
          });
          return;
        }
        setEditionOrdre((actuelle) =>
          actuelle === null
            ? actuelle
            : { ...actuelle, saisie: { ...actuelle.saisie, [cle]: v } },
        );
      }}
      quandEnregistrerOrdre={enregistrerOrdre}
      quandAnnulerEdition={() => setEditionOrdre(null)}
      quandModifierEtape={(id) => {
        if (fiche.nom !== 'prete') return;
        const etape = fiche.fiche.etapes.find((e) => e.id === id);
        if (etape === undefined) return;
        setEditionEtape(editionEtapeVide(etapeDepuis(etape), id));
      }}
      quandBasculerFait={basculerFait}
      affretement={affretement}
      proposes={proposes}
      tousTransporteurs={tousTransporteurs}
      chercheEnCours={chercheEnCours}
      quandAffreter={ouvrirLAffretement}
      quandNouveauTransporteur={ouvrirLeNouveauTransporteur}
      nouveauTransporteur={nouveauTransporteur}
      quandChampNouveauTransporteur={(cle, valeur) => {
        setNouveauTransporteur((courant) => {
          if (courant === null) return null;
          // La faute d'un champ disparaît dès qu'on le retouche : la garder
          // afficherait un reproche à une saisie qui n'existe plus.
          const fautes = { ...courant.fautes };
          delete fautes[cle];
          return { ...courant, saisie: { ...courant.saisie, [cle]: valeur }, fautes };
        });
      }}
      quandNomQuitteNouveauTransporteur={() => {
        setNouveauTransporteur((courant) => {
          if (courant === null) return null;
          // Le code n'est proposé QUE s'il est vide : un code tapé à la main
          // ne se fait pas écraser en quittant le champ Nom.
          if ((courant.saisie['code'] ?? '').trim() !== '') return courant;
          const propose = codePropose(courant.saisie['nom'] ?? '', tousTransporteurs);
          if (propose === '') return courant;
          return { ...courant, saisie: { ...courant.saisie, code: propose } };
        });
      }}
      quandEnregistrerNouveauTransporteur={enregistrerLeNouveauTransporteur}
      quandAnnulerNouveauTransporteur={() => {
        setNouveauTransporteur(null);
        setAction((a) => ({ ...a, refus: null }));
      }}
      quandChampAffretement={(cle, valeur) => {
        setAffretement((a) => (a === null ? null : { ...a, [cle]: valeur }));
      }}
      quandEnregistrerAffretement={enregistrerLAffretement}
      quandAnnulerAffretement={annulerLeAffretement}
      echange={echange}
      quandEchange={(id) => {
        if (fiche.nom !== 'prete') return;
        const etape = fiche.fiche.etapes.find((e) => e.id === id);
        if (etape === undefined) return;
        // Une seule boîte à la fois : ouvrir l'échange referme les deux
        // autres, comme valider referme le litige.
        setValidation(null);
        setLitige(null);
        setAction((a) => ({ ...a, refus: null }));
        setEchange({ etapeId: id, saisie: echangeDepuis(etape) });
      }}
      quandChampEchange={(cle, valeur) => {
        setEchange((a) => (a === null ? null : { ...a, saisie: { ...a.saisie, [cle]: valeur } }));
      }}
      quandEnregistrerEchange={() => {
        enregistrerLEchange(false);
      }}
      quandEffacerEchange={() => {
        enregistrerLEchange(true);
      }}
      resolution={resolution}
      quandResoudre={(litigeId) => {
        setValidation(null);
        setLitige(null);
        setAction((a) => ({ ...a, refus: null }));
        setResolution({
          litigeId,
          texte: '',
          jour: aujourdhui(),
          heure: maintenant(),
        });
      }}
      quandChampResolution={(cle, v) =>
        setResolution((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, [cle]: v },
        )
      }
      quandEnregistrerResolution={enregistrerLaResolution}
      quandLitige={(id) => {
        if (fiche.nom !== 'prete') return;
        setValidation(null);
        setAction((a) => ({ ...a, refus: null }));
        // La boîte propose le premier motif actif s'il y en a un, et le
        // formulaire de CRÉATION d'un motif s'il n'y en a aucun : sans cela,
        // le premier litige de la première société était impossible à ouvrir.
        const premier = motifs.find((m) => m.actif);
        setLitige({
          etapeId: id,
          motifId: premier?.id ?? '',
          motifNouveau: premier === undefined ? '' : null,
          note: '',
          jour: aujourdhui(),
          heure: maintenant(),
        });
      }}
      validation={validation}
      litige={litige}
      tempsPasse={tempsPasse}
      quandTemps={setTempsPasse}
      cout={cout}
      quandRecalculerLeCout={() => {
        if (fiche.nom !== 'prete') return;
        const otId = fiche.id;
        setAction((a) => ({ ...a, envoi: true, refus: null }));
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const fait = await recalculerLeCout(j, otId);
          if (fait.etat !== 'ok' && fait.cause === 'acces') quandAccesPerdu();
          const relu = await lireLeCout(j, otId);
          if (relu.etat === 'ok') setCout(relu.valeur);
          finirLEcriture(fait);
        })();
      }}
      quandChampValidation={(cle, v) =>
        setValidation((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, [cle]: v },
        )
      }
      quandFermerBoite={() => {
        setValidation(null);
        setLitige(null);
        setResolution(null);
        setEchange(null);
        setAffretement(null);
        setAction((a) => ({ ...a, refus: null }));
      }}
      quandConfirmerValidation={confirmerValidation}
      quandChampLitige={(cle, v) =>
        setLitige((actuel) => (actuel === null ? actuel : { ...actuel, [cle]: v }))
      }
      quandNouveauMotif={(nom) =>
        setLitige((actuel) =>
          actuel === null ? actuel : { ...actuel, motifNouveau: nom },
        )
      }
      quandOuvrirLitige={ouvrirLeLitige}
      quandDeplacerEtape={deplacerEtape}
      quandAjouterEtape={(type) => {
        if (fiche.nom !== 'prete') return;
        // Le jour de la dernière étape, ou aujourd'hui : une étape ajoutée
        // tombe presque toujours le même jour que la précédente.
        const derniere = fiche.fiche.etapes[fiche.fiche.etapes.length - 1];
        setEditionEtape(
          editionEtapeVide(etapeVide(type, derniere?.jour ?? aujourdhui()), null),
        );
      }}
      quandChampEtape={(cle, v) => {
        setEditionEtape((actuelle) =>
          actuelle === null
            ? actuelle
            : { ...actuelle, saisie: { ...actuelle.saisie, [cle]: v } },
        );
      }}
      quandSiteEtape={(id) => {
        setEditionEtape((actuelle) =>
          actuelle === null
            ? actuelle
            : { ...actuelle, saisie: { ...actuelle.saisie, ...depuisSite(id) } },
        );
        if (id === '') return;
        void adresseDuSite(id, (adresse) => {
          setEditionEtape((actuelle) =>
            actuelle === null || actuelle.saisie.site_id !== id
              ? actuelle
              : { ...actuelle, saisie: { ...actuelle.saisie, site_adresse: adresse } },
          );
        });
      }}
      quandOuvrirSite={() =>
        setEditionEtape((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, site: SITE_VIDE },
        )
      }
      quandChampSite={(cle, v) =>
        setEditionEtape((actuelle) =>
          actuelle === null || actuelle.site === null
            ? actuelle
            : { ...actuelle, site: { ...actuelle.site, [cle]: v } },
        )
      }
      quandCreerSite={() => {
        if (editionEtape === null) return;
        creerLeSite(editionEtape, setEditionEtape);
      }}
      quandFermerSite={() =>
        setEditionEtape((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, site: null, fautesSite: {} },
        )
      }
      quandEnregistrerEtape={enregistrerEtape}
      quandAnnulerEtape={() => setEditionEtape(null)}
      quandDemanderSuppressionEtape={() =>
        setEditionEtape((actuelle) =>
          actuelle === null ? actuelle : { ...actuelle, confirme: true },
        )
      }
      quandSupprimerEtape={supprimerLEtape}
      quandCreer={() => {
        const jour = aujourdhui();
        setVue('creation');
        setFiche({ nom: 'aucune' });
        setEditionOrdre(null);
        setEditionEtape(null);
        // LES DEUX ÉTAPES SONT DÉJÀ POSÉES — un chargement et une livraison.
        // Ce n'est pas une supposition : 87 des 94 missions de la base v1 en
        // ont exactement deux. Celui qui n'en veut qu'une la retire.
        setCreation({
          ordre: editionVide(ORDRE_VIDE),
          etapes: [
            editionEtapeVide(etapeVide('chargement', jour), null),
            editionEtapeVide(etapeVide('livraison', jour), null),
          ],
        });
      }}
      quandChampEtapeCreation={(rang, cle, v) => {
        setCreation((actuelle) => {
          if (actuelle === null) return actuelle;
          const etapes = actuelle.etapes.map((e, i) =>
            i === rang ? { ...e, saisie: { ...e.saisie, [cle]: v } } : e,
          );
          // Ce qu'on saisit sur un CHARGEMENT se pose sur les livraisons qui
          // n'ont rien — demandé le 04/09/2026. `reporterSurLesLivraisons`
          // n'écrase jamais une valeur déjà saisie.
          if (cle !== 'quantite' && cle !== 'support' && cle !== 'poids') {
            return { ...actuelle, etapes };
          }
          const reportees = reporterSurLesLivraisons(
            etapes.map((e) => e.saisie),
            rang,
          );
          return {
            ...actuelle,
            etapes: etapes.map((e, i) => ({ ...e, saisie: reportees[i] ?? e.saisie })),
          };
        });
      }}
      quandSiteCreation={(rang, id) => {
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                etapes: actuelle.etapes.map((e, i) =>
                  i === rang ? { ...e, saisie: { ...e.saisie, ...depuisSite(id) } } : e,
                ),
              },
        );
        if (id === '') return;
        void adresseDuSite(id, (adresse) => {
          setCreation((actuelle) =>
            actuelle === null
              ? actuelle
              : {
                  ...actuelle,
                  etapes: actuelle.etapes.map((e, i) =>
                    i === rang && e.saisie.site_id === id
                      ? { ...e, saisie: { ...e.saisie, site_adresse: adresse } }
                      : e,
                  ),
                },
          );
        });
      }}
      quandOuvrirSiteCreation={(rang) => majEtapeCreation(rang, { site: SITE_VIDE })}
      quandChampSiteCreation={(rang, cle, v) => {
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                etapes: actuelle.etapes.map((e, i) =>
                  i === rang && e.site !== null
                    ? { ...e, site: { ...e.site, [cle]: v } }
                    : e,
                ),
              },
        );
      }}
      quandCreerSiteCreation={(rang) => {
        const edition = creation?.etapes[rang];
        if (edition === undefined) return;
        creerLeSite(edition, (e) => majEtapeCreation(rang, e));
      }}
      quandFermerSiteCreation={(rang) =>
        majEtapeCreation(rang, { site: null, fautesSite: {} })
      }
      quandAjouterEtapeCreation={(type) =>
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : {
                ...actuelle,
                etapes: [
                  ...actuelle.etapes,
                  // Le jour de la dernière étape saisie : une étape
                  // ajoutée tombe presque toujours le même jour que la
                  // précédente.
                  editionEtapeVide(
                    etapeVide(
                      type,
                      actuelle.etapes[actuelle.etapes.length - 1]?.saisie.jour ??
                        aujourdhui(),
                    ),
                    null,
                  ),
                ],
              },
        )
      }
      quandRetirerEtapeCreation={(rang) =>
        setCreation((actuelle) =>
          actuelle === null
            ? actuelle
            : { ...actuelle, etapes: actuelle.etapes.filter((_, i) => i !== rang) },
        )
      }
      quandDeplacerEtapeCreation={(rang, pas) =>
        setCreation((actuelle) => {
          if (actuelle === null) return actuelle;
          const vers = rang + pas;
          if (vers < 0 || vers >= actuelle.etapes.length) return actuelle;
          const etapes = [...actuelle.etapes];
          const [deplacee] = etapes.splice(rang, 1);
          if (deplacee === undefined) return actuelle;
          etapes.splice(vers, 0, deplacee);
          return { ...actuelle, etapes };
        })
      }
      quandEnregistrerCreation={enregistrerCreation}
    />
  );
}
